import {
  CloudGridYaml,
  DatabaseNeed,
  ExternalDbRequirement,
  GcpRequires,
  NativeEgressRequirement,
  NeedsBlock,
  ParsedNeeds,
  ParsedRequires,
  PersistDeclaration,
  SharedServicesRegistry,
  VectorNeed,
} from './types.js';
import { die } from './errors.js';
import { MIN_SCALE, MAX_SCALE, SPOF_WARNING } from './scale.js';
import {
  checkServiceContentAlignment,
  isHttpModeCronService,
  type ServiceFolderReader,
} from './validate-service-content.js';

const NAME_REGEX = /^[a-z][a-z0-9-]{0,40}[a-z0-9]$/;

const RESERVED_NAMES = [
  'default', 'kube-system', 'kube-public', 'kube-node-lease',
  'n8n', 'sharedgrid', 'sharedgrid-dev', 'cloudgrid', 'cloudgrid-system',
  'istio-system', 'cert-manager', 'ingress-nginx', 'monitoring',
];

const VALID_TYPES = ['node', 'nextjs', 'python', 'static', 'cron'] as const;

const VALID_TIMEZONES = ['UTC', 'EST', 'PST'] as const;

const VALID_GCP_SERVICES = ['bigquery', 'pubsub', 'storage'] as const;

// Backlog 207 — supported `node_version:` values for the static-build
// runner image. Strings only — yaml `20` (integer) is rejected so an
// unintended float (e.g. `20.0`) can't slip through. Default when absent
// is `'22'` (the Node 22 LTS floor set in backlog 204).
const VALID_NODE_VERSIONS = ['18', '20', '22', '24'] as const;

// Backlog 212 phase 2 — known-keys sets for the strict-keys ERROR pass.
// Each set covers ONE nesting level of cloudgrid.yaml. Phase 1 (0.8.14)
// shipped a one-release deprecation window emitting non-blocking warnings;
// phase 2 (0.8.15) flips those to hard errors so typos like `node_versionn:`
// can no longer slip through silently. Keep these in sync with the
// CloudGridYaml / ServiceConfig interfaces in types.ts — adding a yaml
// field without updating these sets means users with typos near that
// field still get suggestions for the OLD field set.
//
// `network` is deprecated (backlog 191) but stays in the known set so
// users mid-migration don't trip the strict-keys error on top of the
// existing deprecation warning. Same for `domain` (backlog 104). Both
// fields are still auto-migrated and deleted from the config object
// before the strict-keys pass runs, so they don't even reach the check.
const TOP_LEVEL_KEYS = new Set<string>([
  'name',
  'domain',
  'custom_domains',
  'description',
  'icon',
  'expose',
  'network',
  'probe',
  'notify',
  'services',
  'requires',
  'auto_depends_on_private_requires',
  'vault',
  'internal_urls',
  // Backlog 192 phase 2 / decision 027a — agent caller model.
  'callers',
  'calls',
  // Backlog 229 — Connectors v1.
  'connectors',
  'hooks',
  'triggers',
  // Dev Grid (spec docs/specs/2026-06-09-cloudgrid-dev-grid.md §M0).
  'dev',
  // Backlog 473 (second slice) — entity-level flag-form persistence.
  'persist',
  // cloudgrid.yaml v2 (founder-finalized vocabulary, 2026-06-25) — the `needs:`
  // capability block. The durable disk (v1 `persist:`) now lives at `needs.disk`;
  // there is no top-level `disk:` or `storage:` field.
  'needs',
  // Entity kind + agent metadata. Documented in CLOUDGRID.md + the CLI/strategy
  // docs as legitimate top-level fields; the validator now accepts them (samples
  // use `kind: inspiration` and the `agent:` block). An agent is an app with an
  // `agent:` block — apps and agents are the same entity type.
  'kind',
  'agent',
  // BL628 — replica-scaling intent (auto | alert | <integer 1-10>).
  'scale',
]);

// Backlog 473 (second slice) — the only key the flag-object form
// (`persist: { size }`) accepts. A typo (`sze:`, `path:`) or a verbose-form
// key under the flag object hard-errors via the strict-keys pass rather than
// silently mis-shaping the claim.
const PERSIST_FLAG_KEYS = new Set<string>(['size']);

// Dev Grid §M0 — strict-keys sets for the `dev:` block. v1 validates exactly
// one field, `dev.snapshot.mask`; unknown keys under `dev` or `dev.snapshot`
// hard-error like every other nesting level.
const DEV_KEYS = new Set<string>(['schema', 'snapshot']);
const DEV_SNAPSHOT_KEYS = new Set<string>(['mask']);

// Dev Grid §M0 — a mask entry is a `collection.field` dotted path: one dot,
// each side lowercase-alphanumeric + underscore, starting with a letter.
// Nested-field masking (more than one dot) is a later grammar extension.
const MASK_ENTRY_REGEX = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;

// Dev Grid §M1 — a `dev.schema` collection name and each of its field names.
// A field value is a flat string array; nested fields are a v2 grammar extension.
// Exported so the `--collections` route validator uses the same grammar — names
// that pass the CLI flag must also pass when promoted into yaml `dev.schema`.
export const DEV_SCHEMA_NAME_REGEX = /^[a-z][a-z0-9_]*$/;

// Backlog 192 phase 2 — strict-keys set for `callers:`. Decision 027a pins
// exactly four sub-keys; anything else is a typo and gets the same hard-
// error treatment as other phase-2 strict-keys violations.
const CALLERS_KEYS = new Set<string>(['policy', 'scope', 'fallback', 'allowed_apps']);

// Decision 027a §"yaml schema" — pinned literal values for the enum-shaped
// callers sub-keys. Kept as constants so the validation messages can
// enumerate them rather than re-encoding the spec.
const CALLERS_POLICY_VALUES = ['auto', 'manual'] as const;
const CALLERS_SCOPE_VALUES = ['org', 'space', 'explicit'] as const;
const CALLERS_FALLBACK_VALUES = ['deny', 'warn'] as const;

// Backlog 229 — known keys per Connectors v1 entry shape. Kept in sync
// with packages/connectors/src/yaml-schema.ts. Unknown sub-keys hard-
// error via the strict-keys pass so typos (`chanel:`, `tmplate:`) can't
// silently mis-shape an entity yaml.
const CONNECTORS_ENTRY_KEYS = new Set<string>(['id', 'target', 'channel']);
const HOOKS_ENTRY_KEYS = new Set<string>(['on', 'via', 'template', 'channel']);
const TRIGGERS_ENTRY_KEYS = new Set<string>(['from', 'event', 'channel', 'route']);

// Backlog 229 — locked enum sets.
const VALID_CONNECTOR_TARGETS = new Set<string>(['system', 'org']);
const VALID_HOOK_TRIGGERS = new Set<string>([
  'release.published',
  'deploy.failed',
  'feedback.received',
  'entity.created',
]);

// Same shape as entity / connector names: lowercase alphanumeric +
// hyphens, 3–40 chars, must start with a letter.
const CONNECTOR_ID_PATTERN = /^[a-z][a-z0-9-]{1,38}[a-z0-9]$/;

const SERVICE_KEYS = new Set<string>([
  'type',
  'lang',
  'build',
  'node_version',
  'base_image',
  'port',
  'source',
  'path',
  'health',
  'env',
  'schedule',
  'run',
  'timezone',
  'depends_on',
  'persist',
  // cloudgrid.yaml v2 (founder Q8) — per-service `needs:` override. The durable
  // disk lives at `needs.disk`; there is no per-service `disk:`/`storage:` field.
  'needs',
]);

// Backlog 473 (first slice) — known keys per `persist:` entry. Strict: a
// typo (`pathh:`, `sze:`) or a not-yet-implemented modifier hard-errors
// rather than silently mis-shaping the claim. `mode` is reserved — only
// the default `pvc` is accepted in the first slice.
const PERSIST_ENTRY_KEYS = new Set<string>(['name', 'path', 'size', 'mode']);

// Backlog 473 — PVC-name-safe slug: lowercase letter first, then lowercase
// alphanumerics and hyphens, terminating on an alphanumeric, 1–40 chars
// total. The terminal `[a-z0-9]` anchor (mirroring CONNECTOR_ID_PATTERN)
// rejects a trailing hyphen — Kubernetes refuses a PVC name ending in `-`
// at apply time with an opaque error, so we catch it at preflight instead.
// The rendered PVC is named `<service>-<name>`; both halves stay within the
// K8s 253-char DNS limit.
const PERSIST_NAME_PATTERN = /^[a-z]([a-z0-9-]{0,38}[a-z0-9])?$/;

// Backlog 473 — container mount paths the platform refuses to back with a
// PVC. Mounting a PVC over `/`, `/proc`, `/sys`, or `/dev` either shadows
// the image root or clobbers kernel pseudo-filesystems. Matched exactly or
// as a path prefix (`/proc/x` is rejected the same as `/proc`).
const PERSIST_FORBIDDEN_PATH_PREFIXES = ['/proc', '/sys', '/dev'];

// Backlog 473 — Kubernetes resource Quantity for storage (decimal or binary
// SI suffix). Mirrors the apimachinery Quantity grammar restricted to the
// suffixes meaningful for `resources.requests.storage`. A bare integer
// (bytes) is allowed; `huge`, `1GB`, and an empty string are rejected.
const K8S_QUANTITY_PATTERN =
  /^[0-9]+(\.[0-9]+)?(Ei|Pi|Ti|Gi|Mi|Ki|E|P|T|G|M|k)?$/;

function isK8sQuantity(value: string): boolean {
  if (!K8S_QUANTITY_PATTERN.test(value)) return false;
  // A pure-zero request (`0`, `0Gi`) is a valid Quantity but a useless
  // claim — reject it so the user gets a clear error instead of an
  // unschedulable 0-byte PVC.
  return parseFloat(value) > 0;
}

const SERVICE_BUILD_KEYS = new Set<string>(['command', 'output', 'env']);

// Backlog 207 part 3 — keys the platform owns at build time and which the
// user must not set via `services.<svc>.build.env:`. Subset of the runtime
// reserved keys (kubernetes.ts `PLATFORM_KEY_PATTERNS`).
//
// Exact matches: PORT, APP_NAME, SERVICE_NAME, NODE_ENV — naming a build-
// time var with one of these would shadow the runtime values the build
// might inadvertently inherit and confuse build scripts that bridge the
// two (e.g. a build that reads NODE_ENV to switch optimisation modes).
// Prefix: CLOUDGRID_* — every platform-injected env var lives under this
// namespace, and the platform may inject build-time CLOUDGRID_* vars in
// the future without breaking existing yamls.
//
// `_URL` suffix is NOT reserved at build time. Runtime _URL keys exist
// because the platform auto-injects sibling and resource URLs into pods;
// the Cloud Build step is a different container with no sibling URLs to
// shadow. Reserving _URL would block the canonical use case
// (`VITE_API_URL`, `NEXT_PUBLIC_API_URL`) — those are exactly the names
// `build.env` is designed to set.
const BUILD_ENV_RESERVED_EXACT = new Set<string>([
  'PORT',
  'APP_NAME',
  'SERVICE_NAME',
  'NODE_ENV',
]);

function isReservedBuildEnvKey(key: string): boolean {
  if (BUILD_ENV_RESERVED_EXACT.has(key)) return true;
  if (key.startsWith('CLOUDGRID_')) return true;
  return false;
}

// Backlog 207 part 3 — env var name shape. UPPER_SNAKE_CASE; first character
// a letter or underscore. Decision 010 keeps platform-injected vars visually
// distinct from yaml's runtime `env:`, which accepts lowercase. Build-time
// env applies the same upper-only convention so misnaming is rejected up
// front.
const BUILD_ENV_NAME_PATTERN = /^[A-Z_][A-Z0-9_]*$/;

const NETWORK_KEYS = new Set<string>(['public']);

const PROBE_KEYS = new Set<string>(['path', 'expected']);

// BL572 — strict-keys for the object form of `services.<svc>.health`. Same
// shape as the top-level `probe:` block; an unknown sibling key (`pat:`,
// `expect:`) hard-errors via checkUnknownKeys rather than silently dropping.
const HEALTH_KEYS = new Set<string>(['path', 'expected']);

// BL572 — the `expected:` grammar the HTTP probe (`http-probe.ts`) actually
// understands: the default `'healthy'`, the legacy lenient `'<500'`, a
// specific 3-digit code (`'200'`), or a class (`'2xx'`). YAML may parse a bare
// `200` as a number, so the validator coerces number→string before matching.
const HEALTH_EXPECTED_REGEX = /^(healthy|<500|[1-5]\d{2}|[1-5]xx)$/;

// `requires` items in object form accept `gcp: {...}` (the only multi-field
// object modifier) or any `<name>: private` shorthand. Shorthand entries are
// filtered out before reaching the strict-keys check, so the only known
// modifier key seen here is `gcp`.
const REQUIRES_ITEM_KEYS = new Set<string>(['gcp']);

// DB W1.C (spec 2026-06-06 §2) — yaml-level alias for the platform NoSQL
// kind: `db` → mongodb. Parsed at validate time only; shared-services.yaml
// keeps the canonical registry name. The Redis-shaped resource keeps its
// `redis` keyword everywhere (founder decision: zero migration of the live
// `{redis: private}` apps; honest substrate naming — no `cache` alias).
const REQUIRES_KIND_ALIASES: Record<string, string> = {
  db: 'mongodb',
};

// DB W1.C — keys allowed in the `{<name>: private}` shorthand. `mongodb`
// passes strict-keys so the dedicated MONGODB_PRIVATE_DEPRECATED die can
// name the replacement; anything else (e.g. a typo, or the dropped `cache`
// keyword) gets the strict-keys unknown-field error.
const REQUIRES_PRIVATE_SHORTHAND_KEYS = new Set<string>(['db', 'redis', 'mongodb']);

// DB W1.C — strict-keys sets for the two multi-key requires shapes added by
// spec §2.1: `{db|redis: external, secret: X}` and
// `{external_db: native, port: N}`.
const REQUIRES_EXTERNAL_ITEM_KEYS = new Set<string>(['db', 'redis', 'secret']);
const REQUIRES_NATIVE_EGRESS_KEYS = new Set<string>(['external_db', 'port']);

// BL579 M2 — the managed vector substrate keyword + the engines the platform
// can provision today. `pgvector` is the only v1 engine; the union is left open
// (string list, not a single literal) so future engines (`redis-vss`, `qdrant`,
// …) extend here without a structural change. Unknown engines are rejected at
// validate time (`validateVectorRequires`), mirroring the GCP-service allowlist.
export const VECTOR_REQUIRES_KEYWORD = 'vector';
export const SUPPORTED_VECTOR_ENGINES = ['pgvector'] as const;
export type VectorEngine = (typeof SUPPORTED_VECTOR_ENGINES)[number];
// Strict-keys set for the top-level vector entry `{ vector: ... }` — `vector`
// is the only allowed key (no sibling modifiers in M2).
const REQUIRES_VECTOR_ITEM_KEYS = new Set<string>([VECTOR_REQUIRES_KEYWORD]);
// Strict-keys set for the object form `{ vector: { engine: pgvector, dim: N } }`.
// `engine` selects the substrate; `dim` is a forward-compat embedding-dimension
// hint (BL579 M2-wiring) — persisted but not consumed by the v1 provisioner. A
// typo (`engin:`, `dimension:`) still lands a Levenshtein suggestion instead of
// being silently dropped.
const REQUIRES_VECTOR_OPTION_KEYS = new Set<string>(['engine', 'dim']);

// ───────────────────────────────────────────────────────────────────────────
// cloudgrid.yaml v2 — the `needs:` capability block (founder-finalized
// vocabulary, 2026-06-25 evening). Strict-keys sets + value enums. The nine
// semantic needs are SEMANTIC CAPABILITIES, not engine names. The substrate
// needs (`database`, the Redis-backed `cache`/`kv`/`queue`/`pubsub`, and
// `vector`) translate back onto the v1 `requires:` wire (see needsToRequires)
// so the deployer is unchanged; `object_storage`, `disk`, and `ai` are carried
// on {@link ParsedNeeds} for the deployer-wiring follow-ups (BL584 M2).
// ───────────────────────────────────────────────────────────────────────────
const NEEDS_KEYS = new Set<string>([
  'database',
  'cache',
  'kv',
  'queue',
  'pubsub',
  'vector',
  'object_storage',
  'disk',
  'ai',
]);

// The four Redis-backed needs share one object grammar (`{ engine, secret }`)
// and one physical Redis under the hood; the env var + maxmemory-policy differ.
const REDIS_NEED_KEYS_SET = ['cache', 'kv', 'queue', 'pubsub'] as const;
type RedisNeedKey = (typeof REDIS_NEED_KEYS_SET)[number];

const DATABASE_NEED_KEYS = new Set<string>(['tier', 'secret']);
const REDIS_NEED_KEYS = new Set<string>(['engine', 'secret']);
const VECTOR_NEED_KEYS = new Set<string>(['engine', 'dim']);
const OBJECT_STORAGE_NEED_KEYS = new Set<string>(['size']);
const DISK_NEED_KEYS = new Set<string>(['size', 'mount']);

const VALID_DATABASE_TIERS = ['pool', 'dedicated', 'external'] as const;
// Engine pin for the Redis-backed needs: `redis` (platform) or `external` (BYOC).
const VALID_REDIS_NEED_ENGINES = ['redis', 'external'] as const;

// DB W1.C (spec §1.2 #9 / kickoff C) — recognized external-DB native ports.
// Mongo, Postgres (+alt), MySQL (+alt), Redis (+alts). Anything else needs a
// platform-admin conversation; egress is default-closed (HTTPS 443/80 only).
const EXTERNAL_NATIVE_DB_PORTS = [27017, 5432, 3306, 6379, 6380, 6381, 5433, 3307];

// DB W1.C (spec §2.3 / §1.2 #8) — the historical `{mongodb: private}` form is
// rejected, not silently converted: users should update their yaml to the
// unified `{db: private}` keyword. The code prefix gives CLI/CI a stable
// grep handle, mirroring the VAULT_* code-in-message convention.
const MONGODB_PRIVATE_DEPRECATED_MESSAGE =
  'MONGODB_PRIVATE_DEPRECATED: `{mongodb: private}` is no longer supported. ' +
  'Use `{db: private}` instead — the platform-provided NoSQL DB is now ' +
  'uniformly Mongo and the keyword has been unified.';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Backlog 212 phase 1 — Levenshtein distance ≤ 1 check between two strings.
 * Returns true when `a` can be transformed into `b` with at most one
 * insertion, deletion, or substitution. Used to surface a fuzzy-match
 * suggestion when a yaml field name doesn't match any known key at its
 * level (e.g. `node_versionn` → `node_version`).
 *
 * Distance > 1 (e.g. `serivces` → `services` is distance 2 by transposition,
 * which decomposes into 2 substitutions) returns false. That's intentional:
 * a wider net would surface false matches more often than it would help, and
 * the warning still includes the full "Known fields at <level>:" enumeration
 * for cases the fuzzy match misses.
 */
function levenshteinAtMostOne(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  // Same length → look for exactly one substitution.
  if (a.length === b.length) {
    let diffs = 0;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        diffs++;
        if (diffs > 1) return false;
      }
    }
    return diffs === 1;
  }
  // Off-by-one length → look for exactly one insertion (in the shorter
  // string) or deletion. Walk both with a single divergence allowed.
  const [shorter, longer] = a.length < b.length ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let diverged = false;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] === longer[j]) {
      i++;
      j++;
      continue;
    }
    if (diverged) return false;
    diverged = true;
    j++; // skip one char in the longer string
  }
  return true;
}

function suggestKey(unknownKey: string, known: Set<string>): string | undefined {
  for (const k of known) {
    if (levenshteinAtMostOne(unknownKey.toLowerCase(), k.toLowerCase())) return k;
  }
  return undefined;
}

/**
 * Backlog 277 — full Levenshtein edit distance between two strings. Unlike
 * `levenshteinAtMostOne` (a cheap boolean used for yaml KEY suggestions where
 * a distance-1 cutoff is deliberate), this returns the real distance so
 * close-match suggestions for fixed-vocabulary VALUES — a shared-service
 * name, a `type:`, a GCP service — can be ranked closest-first.
 */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  // Single-row DP. `prev[j]` is the distance for the previous row.
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  let curr = new Array<number>(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1, // deletion
        curr[j - 1] + 1, // insertion
        prev[j - 1] + cost, // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/**
 * Backlog 277 — known values within editing distance of `input`, closest
 * first. The distance cap adapts to word length: short words (where any edit
 * is proportionally large) get a tighter cap, so `ai` does NOT spuriously
 * match `n8n`, while `reddis` still matches `redis`. Capped at 3 absolute per
 * the backlog spec. Ties break alphabetically so output is deterministic.
 */
function closeMatches(
  input: string,
  candidates: Iterable<string>,
  limit = 3,
): string[] {
  const lower = input.toLowerCase();
  const scored: { value: string; distance: number }[] = [];
  for (const candidate of candidates) {
    const distance = levenshtein(lower, candidate.toLowerCase());
    const cap = Math.min(3, Math.max(1, Math.floor(Math.max(input.length, candidate.length) / 2)));
    if (distance <= cap) scored.push({ value: candidate, distance });
  }
  scored.sort((x, y) => x.distance - y.distance || x.value.localeCompare(y.value));
  return scored.slice(0, limit).map((s) => s.value);
}

/**
 * Backlog 277 — render a blameless "Did you mean ...?" line for a rejected
 * fixed-vocabulary value, or '' when there are no close matches so callers
 * can append unconditionally. §23 voice — plain, no exclamation marks.
 */
function didYouMeanLine(input: string, candidates: Iterable<string>): string {
  const matches = closeMatches(input, candidates);
  if (matches.length === 0) return '';
  if (matches.length === 1) return `Did you mean '${matches[0]}'?`;
  return `Did you mean one of: ${matches.join(', ')}?`;
}

function checkUnknownKeys(
  obj: Record<string, unknown>,
  known: Set<string>,
  pathLabel: string,
): void {
  for (const key of Object.keys(obj)) {
    if (known.has(key)) continue;
    const suggestion = suggestKey(key, known);
    die(formatUnknownKeyError(pathLabel, key, suggestion, known));
  }
}

/**
 * Backlog 212 phase 2 — error text for an unknown yaml key. Wording
 * follows §23 voice — plain, specific, confident, no exclamation marks.
 * Phase 1 (0.8.14) emitted this as a non-blocking warning with a one-
 * release window for users to clean up; phase 2 (0.8.15) turns it into
 * a hard error so silently-ignored typos can no longer slip through.
 */
function formatUnknownKeyError(
  pathLabel: string,
  unknownKey: string,
  suggestion: string | undefined,
  known: Set<string>,
): string {
  const sortedKnown = Array.from(known).sort();
  const lines: string[] = [];
  lines.push(`Unknown field '${unknownKey}' at ${pathLabel}.`);
  lines.push('');
  if (suggestion) {
    lines.push(`  Did you mean '${suggestion}'?`);
    lines.push('');
  }
  lines.push(`  Known fields at ${pathLabel}:`);
  lines.push(`    ${sortedKnown.join(', ')}`);
  lines.push('');
  lines.push(`  Fix the typo or remove the field. Unknown fields are rejected`);
  lines.push(`  to prevent silent yaml drift.`);
  return lines.join('\n');
}

// Backlog 104 — single-label FQDN regex. Each label is 1–63 chars,
// lowercase alphanumeric + hyphens, no leading/trailing hyphen.
const FQDN_LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

function isValidFqdn(fqdn: string): boolean {
  if (fqdn.length === 0 || fqdn.length > 253) return false;
  if (fqdn.includes('*')) return false;
  if (/^\d+(\.\d+){3}$/.test(fqdn)) return false; // IPv4
  const labels = fqdn.split('.');
  if (labels.length < 2) return false;
  return labels.every((l) => FQDN_LABEL.test(l));
}

export function validateName(name: string, label: string): void {
  // BL531 — hoisted typeof guard. The runtime type doesn't always match the
  // signature (yaml `name:` is optional + the parsed value can be any JS type
  // when callers bypass zod). `NAME_REGEX.test` coerces non-strings via
  // String(), so an array like ['foo--bar'] would pass the regex AND skip the
  // `--` check below (`typeof === 'string'` is false for arrays). Hoisting the
  // check seals both gaps uniformly.
  if (typeof name !== 'string') return;
  if (!NAME_REGEX.test(name)) {
    die(`Invalid ${label}: ${name} (must be 2-42 chars, lowercase alphanumeric + hyphens, no trailing hyphen)`);
  }
  // BL522 / BL484 — `--` is the reserved separator for the flat platform host
  // `<entity>--<org>.cloudgrid.io`, so no slug may contain it.
  if (name.includes('--')) {
    die(`Invalid ${label}: ${name} (names can't contain '--', which is reserved as the host separator)`);
  }
  if (label === 'app name' && RESERVED_NAMES.includes(name)) {
    die(`Reserved ${label}: ${name} (conflicts with system namespace)`);
  }
}

export interface ValidateOptions {
  /**
   * Entity kind from `.cloudgrid/link.json` (CLI) or the Firestore Entity
   * record (server). When `'app'` (the default for legacy callers that pass
   * no `kind`), backlog 082's routability check fires: the yaml must
   * declare ≥1 service with `path:`, OR opt out via
   * `network.public: false`. When `'agent'` or omitted, the routability
   * check is skipped (agents may be HTTP-mode cron only or scheduled;
   * legacy callers without kind context shouldn't be retroactively rejected
   * — server-side and CLI preflight pass kind explicitly).
   */
  kind?: 'app' | 'agent';
  /**
   * Backlog 086 — per-service folder reader factory. When provided, every
   * service runs the type↔content alignment check (decision 020 §3): a
   * `type: nextjs` service must have a `next.config.*`, a `type: python`
   * must have `requirements.txt` or `pyproject.toml`, etc. CLI preflight
   * passes an fs-based reader; the API passes a tarball-based reader.
   * The factory may return `null` for a service with no folder context
   * (e.g. rollback re-runs that don't re-upload sources) — that service
   * skips alignment and the validation continues. HTTP-mode cron services
   * always skip alignment regardless (backlog 076).
   */
  serviceReader?: (serviceName: string) => ServiceFolderReader | null;
  /**
   * Backlog 092 — soft-warning channel. Called with a multi-line message
   * when a non-cron service in a kind=app entity omits `path:`. The yaml
   * still validates successfully; the warning suggests the explicit
   * `path: false` form so the trace and the renderer report can name the
   * developer's intent rather than infer it from absence-of-field.
   *
   * Callers that don't pass `warn` simply skip the soft warning — the
   * yaml is still accepted. CLI preflight wires this to `process.stderr`;
   * server-side validation typically leaves it undefined so platform logs
   * don't accumulate per-deploy advisories.
   */
  warn?: (message: string) => void;
  /**
   * cloudgrid.yaml v2 (founder Q5–Q8, 2026-06-25) — structured deprecation
   * channel. Called once per deprecated v1 field encountered (`requires:` or
   * `persist:`) during the accept-both transition window. Carries a stable
   * telemetry code so the CLI / API can forward it into the `cli_events`
   * pipeline (BL581 substrate + BL593 emit) as a
   * `cli.warn.deprecated_yaml_field` event without re-parsing the message.
   *
   * Distinct from {@link warn}: `warn` is the human-readable CLI advisory;
   * this is the machine-readable signal. validate.ts lives in `packages/shared`
   * and cannot write to Firestore directly, so it surfaces the event via this
   * callback and leaves the actual telemetry write to the caller.
   */
  onDeprecatedField?: (event: DeprecatedFieldEvent) => void;
}

/**
 * cloudgrid.yaml v2 — a deprecated v1 field encountered during the accept-both
 * transition window. Fed to {@link ValidateOptions.onDeprecatedField}.
 */
export interface DeprecatedFieldEvent {
  /** The deprecated v1 field. `persist` is the v1 name for the durable disk. */
  field: 'requires' | 'persist';
  /**
   * The canonical v2 replacement. `requires:` → the `needs:` block;
   * `persist:` → `needs.disk` (the durable disk now lives inside `needs:`).
   */
  replacement: 'needs' | 'needs.disk';
  /** Stable telemetry code for the `cli_events` pipeline. */
  code: 'deprecated_yaml_field';
}

/**
 * Backlog 473 — validate a service's `persist:` value and the single-replica
 * constraint it carries. Runs as preflight in BOTH the CLI (`validateConfig`)
 * and the server deploy path (deployer.ts calls `validateConfig`); a thrown
 * `CloudGridError` becomes a 400 there.
 *
 * Accepts (second slice — the type is now a union):
 *   - `true` — flag form, system-chosen path + default size.
 *   - `{ size }` — flag form with a size override.
 *   - `[{ name, path, size, mode? }, ...]` — verbose array form (first slice).
 *   - `false` / absent — no persistence (pre-473 back-compat).
 *
 * Enforced:
 *   - `persist:` (any active form) + a user-declared `replicas > 1` is
 *     rejected (file-based DBs are single-writer) with the §23-voice message
 *     naming `requires: [db]` as the multi-writer path.
 *   - flag-object form: only `size`, a positive K8s Quantity.
 *   - verbose form: each entry is a strict object of {name, path, size, mode?};
 *     `name` is a PVC-name-safe slug; `path` is an absolute, non-system path;
 *     `size` is a positive K8s Quantity; `mode` (if set) is `pvc`; `name` and
 *     `path` are each unique within the service.
 *
 * The cross-form checks (entity-level vs service-level mixing, ambiguous
 * primary) live in `validateConfig`, which has the whole config in scope.
 *
 * No-op (returns immediately) when the service declares no `persist:` — the
 * pre-473 back-compat path. Called BEFORE the per-service strict-keys check
 * so the replicas message wins over a generic "unknown field 'replicas'".
 */
export function validatePersist(serviceName: string, svc: unknown): void {
  if (!isPlainObject(svc)) return;
  const rawPersist = svc['persist'];
  // Absent or an explicit opt-out (`persist: false`) → no persistence; nothing
  // to validate, and the replicas gate below must not fire.
  if (rawPersist === undefined || rawPersist === false) return;

  // The single-replica gate fires before any shape check so a multi-replica
  // declaration is rejected with the migration path regardless of whether
  // the persist entries themselves are well-formed. Applies to every active
  // form (flag or verbose) — all of them pin the service to one replica.
  const rawReplicas = svc['replicas'];
  if (typeof rawReplicas === 'number' && rawReplicas > 1) {
    die(
      'Persist requires a single replica. Multi-replica apps with ' +
      'file-based databases corrupt — migrate to `requires: [db]` for a ' +
      'multi-writer Mongo from the Pool/Dedicated/External tier.',
    );
  }

  // Backlog 473 (second slice) — flag forms. `true` is the bare flag (default
  // size, system-chosen path); a plain object is the `{ size }` override.
  // Both materialize one platform-chosen volume + `CLOUDGRID_PERSIST_PATH` in
  // the renderer; here we only validate the optional size.
  if (rawPersist === true) return;
  if (isPlainObject(rawPersist)) {
    validatePersistFlagObject(rawPersist, `services.${serviceName}.persist`, `Service '${serviceName}'`);
    return;
  }

  // Verbose array form (first slice) — developer-chosen named volumes.
  if (!Array.isArray(rawPersist)) {
    die(
      `Service '${serviceName}' has an invalid 'persist:'. Use 'true', a ` +
      'size override like { size: 1Gi }, or a list of { name, path, size } ' +
      'volumes.',
    );
  }

  const seenNames = new Set<string>();
  const seenPaths = new Set<string>();

  for (const entry of rawPersist) {
    if (!isPlainObject(entry)) {
      die(
        `Service '${serviceName}' has a 'persist:' entry that is not an ` +
        'object. Each entry needs a name, path, and size.',
      );
    }
    checkUnknownKeys(entry, PERSIST_ENTRY_KEYS, `services.${serviceName}.persist`);

    const { name, path, size, mode } = entry as {
      name?: unknown;
      path?: unknown;
      size?: unknown;
      mode?: unknown;
    };

    if (typeof name !== 'string' || name.length === 0) {
      die(
        `Service '${serviceName}' has a 'persist:' entry missing 'name'. ` +
        'Each entry needs a name (the volume slug), a path, and a size.',
      );
    }
    if (!PERSIST_NAME_PATTERN.test(name)) {
      die(
        `Service '${serviceName}' has a 'persist:' entry with invalid name ` +
        `'${name}'. Use lowercase letters, digits, and hyphens, starting ` +
        'with a letter and ending with a letter or digit — no trailing ' +
        'hyphen (1–40 characters).',
      );
    }

    if (typeof path !== 'string' || path.length === 0) {
      die(
        `Service '${serviceName}' persist entry '${name}' is missing ` +
        "'path'. Set the absolute mount path, e.g. /app/data.",
      );
    }
    if (!path.startsWith('/')) {
      die(
        `Service '${serviceName}' persist entry '${name}' has a relative ` +
        `path '${path}'. Use an absolute path starting with '/', ` +
        'e.g. /app/data.',
      );
    }
    if (isForbiddenPersistPath(path)) {
      die(
        `Service '${serviceName}' persist entry '${name}' mounts a system ` +
        `path '${path}'. Choose a path under your app directory, ` +
        'e.g. /app/data.',
      );
    }

    if (typeof size !== 'string' || size.length === 0) {
      die(
        `Service '${serviceName}' persist entry '${name}' is missing ` +
        "'size'. Set a Kubernetes quantity such as 1Gi or 500Mi.",
      );
    }
    if (!isK8sQuantity(size)) {
      die(
        `Service '${serviceName}' persist entry '${name}' has invalid size ` +
        `'${size}'. Use a Kubernetes quantity such as 1Gi or 500Mi.`,
      );
    }

    if (mode !== undefined && mode !== 'pvc') {
      die(
        `Service '${serviceName}' persist entry '${name}' has unsupported ` +
        `mode ${JSON.stringify(mode)}. Only 'pvc' is supported in this ` +
        'release; litestream and blob modes are follow-ups.',
      );
    }

    if (seenNames.has(name)) {
      die(
        `Service '${serviceName}' has two 'persist:' entries named ` +
        `'${name}'. Each persist name must be unique within a service.`,
      );
    }
    seenNames.add(name);

    if (seenPaths.has(path)) {
      die(
        `Service '${serviceName}' has two 'persist:' entries mounting ` +
        `'${path}'. Each persist path must be unique within a service.`,
      );
    }
    seenPaths.add(path);
  }
}

/**
 * Backlog 473 (second slice) — validate the flag-object form `{ size }`. Used
 * for both service-level (`services.<name>.persist: { size }`) and entity-level
 * (`persist: { size }`) declarations. `size` is optional (absent → the platform
 * default); when present it must be a positive K8s Quantity. Unknown keys
 * hard-error so a verbose-form key under the flag object (e.g. a stray `path:`)
 * is caught rather than silently dropped.
 *
 * @param keysPath  strict-keys path label (e.g. `services.api.persist` or `persist`).
 * @param subject   message prefix (e.g. `Service 'api'` or `Entity-level`).
 */
function validatePersistFlagObject(
  obj: Record<string, unknown>,
  keysPath: string,
  subject: string,
): void {
  checkUnknownKeys(obj, PERSIST_FLAG_KEYS, keysPath);
  const { size } = obj as { size?: unknown };
  if (size !== undefined && (typeof size !== 'string' || size.length === 0 || !isK8sQuantity(size))) {
    die(
      `${subject} persist size ${JSON.stringify(size)} is not a Kubernetes ` +
      'quantity such as 1Gi or 500Mi.',
    );
  }
}

/**
 * Backlog 473 (second slice) — whether a service declares an ACTIVE `persist:`
 * (any form except absent / `false`). Used by the entity-level cross-checks to
 * detect double-declaration.
 */
function serviceHasActivePersist(svc: unknown): boolean {
  if (!isPlainObject(svc)) return false;
  const p = (svc as { persist?: unknown }).persist;
  return p !== undefined && p !== false;
}

/**
 * Backlog 473 (second slice) — services that expose a port, i.e. could be the
 * entity's primary. A "port-exposing" service is any non-cron service — every
 * web service binds the container port (DEFAULT_CONTAINER_PORT or its `port:`
 * override), while a `type: cron` service has no listening port. The renderer's
 * defensive `resolvePrimaryService` uses the same definition, so the validation
 * rejection and the render-time backstop agree on what "ambiguous" means.
 */
function portExposingServices(config: CloudGridYaml): string[] {
  return Object.keys(config.services).filter((s) => config.services[s].type !== 'cron');
}

/**
 * Backlog 473 (second slice) — validate the entity-level `persist:` flag and
 * its cross-form constraints. Entity-level persist is the flag form only
 * (`true` / `{ size }`); the verbose array form stays service-level. The
 * deployer attaches it to the entity's single primary (port-exposing) service.
 *
 * Rejected (§23 voice):
 *   - the verbose array form at entity level → points at the service-level form.
 *   - entity-level AND any service-level `persist:` on the same entity.
 *   - entity-level persist when the primary is ambiguous (zero or 2+
 *     port-exposing services) → PERSIST_AMBIGUOUS_PRIMARY nudge.
 *
 * No-op when the entity declares no `persist:` or opts out with `false`.
 */
function validateEntityPersist(config: CloudGridYaml): void {
  const rawPersist = (config as { persist?: unknown }).persist;
  if (rawPersist === undefined || rawPersist === false) return;

  // Entity-level is the simple flag form. The verbose array form is per-service.
  if (Array.isArray(rawPersist)) {
    die(
      'Entity-level persist is the simple flag form (persist: true or ' +
      'persist: { size: 1Gi }). For multiple named volumes, declare persist on ' +
      'the service: services.<name>.persist: [{ name, path, size }].',
    );
  }
  if (rawPersist !== true) {
    if (!isPlainObject(rawPersist)) {
      die('Entity-level persist must be true or a size override like { size: 1Gi }.');
    }
    validatePersistFlagObject(rawPersist, 'persist', 'Entity-level');
  }

  // Declaring both levels makes ownership ambiguous — reject with the §23 nudge.
  if (Object.values(config.services).some(serviceHasActivePersist)) {
    die('Persist is declared twice. Use either entity-level or service-level, not both.');
  }

  // Resolve the primary (the single non-cron service). Zero and 2+ are both
  // failures, but the advice differs: with no candidate there's nothing to
  // move the declaration onto, so we nudge toward adding a service or
  // declaring persist on the cron service directly; with 2+ the primary is
  // ambiguous, so we nudge toward the service-level form.
  const candidates = portExposingServices(config);
  if (candidates.length === 0) {
    die(
      'Entity-level persist needs at least one non-cron service. Add a web ' +
      'or worker service, or declare persist at service level on the cron ' +
      'service directly (services.<name>.persist: true).',
    );
  }
  if (candidates.length > 1) {
    die(
      'Entity-level persist needs a single primary service. Move the ' +
      'declaration into the service that should have storage ' +
      '(services.<name>.persist: true).',
    );
  }
}

/**
 * Backlog 473 — whether a mount path collides with a system path the
 * platform refuses to back with a PVC. `/` is rejected outright; the
 * pseudo-filesystem roots are rejected as exact matches or path prefixes.
 */
function isForbiddenPersistPath(path: string): boolean {
  if (path === '/') return true;
  for (const prefix of PERSIST_FORBIDDEN_PATH_PREFIXES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return true;
  }
  return false;
}

// ───────────────────────────────────────────────────────────────────────────
// cloudgrid.yaml v2 — `needs:` validation + normalization (founder-finalized
// vocabulary, 2026-06-25 evening). The substrate needs (`database`, the four
// Redis-backed `cache`/`kv`/`queue`/`pubsub`, and `vector`) translate back onto
// the v1 `requires:` wire so the deployer/generators stay unchanged; the
// capability needs (`object_storage`, `disk`, `ai`) are recorded on
// {@link ParsedNeeds} for the deployer-wiring follow-ups (BL584 M2). All
// messages follow §23 voice — plain, specific, no exclamation marks.
// ───────────────────────────────────────────────────────────────────────────

/** v2 — §23-voice human advisory for a deprecated v1 field. */
function formatDeprecatedFieldWarning(
  field: DeprecatedFieldEvent['field'],
  replacement: DeprecatedFieldEvent['replacement'],
): string {
  return (
    `'${field}:' is deprecated. Use '${replacement}:' instead — the new ` +
    'cloudgrid.yaml shape. Both parse during the transition window; update ' +
    'your yaml at your earliest convenience.'
  );
}

/** v2 — fire both the structured telemetry callback and the human advisory. */
function fireDeprecation(
  opts: ValidateOptions,
  field: DeprecatedFieldEvent['field'],
  replacement: DeprecatedFieldEvent['replacement'],
): void {
  if (opts.onDeprecatedField) {
    opts.onDeprecatedField({ field, replacement, code: 'deprecated_yaml_field' });
  }
  if (opts.warn) opts.warn(formatDeprecatedFieldWarning(field, replacement));
}

/** v2 — known vector engine assertion shared by the `needs.vector` forms. */
function assertVectorNeedEngine(raw: unknown, label: string): void {
  if (
    typeof raw !== 'string' ||
    !(SUPPORTED_VECTOR_ENGINES as readonly string[]).includes(raw)
  ) {
    const hint =
      typeof raw === 'string' ? didYouMeanLine(raw, [...SUPPORTED_VECTOR_ENGINES]) : '';
    die(
      `Unknown vector engine ${JSON.stringify(raw)} at '${label}'. ` +
        `Supported engines: ${SUPPORTED_VECTOR_ENGINES.join(', ')}.` +
        (hint ? ` ${hint}` : ''),
    );
  }
}

/**
 * v2 — validate one `needs.database` value. Accepts `true`/`false` (grid decides
 * the tier), a bare tier enum, or `{ tier, secret }`. `external` needs a vault
 * secret. Engine is always Mongo (Decision 035).
 */
function validateDatabaseNeed(db: unknown, label: string): void {
  if (typeof db === 'boolean') return;
  if (typeof db === 'string') {
    if (!(VALID_DATABASE_TIERS as readonly string[]).includes(db)) {
      const hint = didYouMeanLine(db, VALID_DATABASE_TIERS);
      die(
        `'${label}' must be true, false, or one of ${VALID_DATABASE_TIERS.join(', ')}.` +
          (hint ? ` ${hint}` : ''),
      );
    }
    if (db === 'external') {
      die(
        `'${label}: external' needs a vault secret. Use the object form ` +
          '{ tier: external, secret: MY_MONGODB_URL } naming a key in your ' +
          'vault: block.',
      );
    }
    return;
  }
  if (isPlainObject(db)) {
    checkUnknownKeys(db, DATABASE_NEED_KEYS, label);
    const tier = db['tier'];
    if (typeof tier !== 'string' || !(VALID_DATABASE_TIERS as readonly string[]).includes(tier)) {
      const hint = typeof tier === 'string' ? didYouMeanLine(tier, VALID_DATABASE_TIERS) : '';
      die(
        `'${label}.tier' must be one of ${VALID_DATABASE_TIERS.join(', ')}.` +
          (hint ? ` ${hint}` : ''),
      );
    }
    const secret = db['secret'];
    if (tier === 'external') {
      if (typeof secret !== 'string' || secret.length === 0) {
        die(
          `'${label}.tier: external' requires 'secret:' naming a key in your ` +
            'vault: block.',
        );
      }
    } else if (secret !== undefined) {
      die(`'${label}.secret' is only valid when tier is external.`);
    }
    return;
  }
  die(
    `'${label}' must be true, false, a tier (${VALID_DATABASE_TIERS.join(', ')}), ` +
      'or a { tier, secret } object.',
  );
}

/**
 * v2 — validate one Redis-backed need value (`cache`, `kv`, `queue`, `pubsub`).
 * Accepts `true`/`false`, the engine pin `redis`, or `{ engine, secret }` where
 * `engine` is `redis` (platform) or `external` (BYOC, requires `secret:`). The
 * four needs share one physical Redis; the eviction policy + env var differ
 * (handled downstream by the deployer, BL584 M2).
 */
function validateRedisNeed(raw: unknown, label: string): void {
  if (typeof raw === 'boolean') return;
  if (typeof raw === 'string') {
    if (raw !== 'redis') {
      const hint = didYouMeanLine(raw, VALID_REDIS_NEED_ENGINES);
      die(
        `'${label}' must be true, false, 'redis', or a { engine, secret } object.` +
          (hint ? ` ${hint}` : ''),
      );
    }
    return;
  }
  if (isPlainObject(raw)) {
    checkUnknownKeys(raw, REDIS_NEED_KEYS, label);
    const engine = raw['engine'];
    if (
      engine !== undefined &&
      (typeof engine !== 'string' ||
        !(VALID_REDIS_NEED_ENGINES as readonly string[]).includes(engine))
    ) {
      const hint = typeof engine === 'string' ? didYouMeanLine(engine, VALID_REDIS_NEED_ENGINES) : '';
      die(
        `'${label}.engine' must be one of ${VALID_REDIS_NEED_ENGINES.join(', ')}.` +
          (hint ? ` ${hint}` : ''),
      );
    }
    const secret = raw['secret'];
    if (engine === 'external') {
      if (typeof secret !== 'string' || secret.length === 0) {
        die(`'${label}.engine: external' requires 'secret:' naming a key in your vault: block.`);
      }
    } else if (secret !== undefined) {
      die(`'${label}.secret' is only valid when engine is external.`);
    }
    return;
  }
  die(`'${label}' must be true, false, 'redis', or a { engine, secret } object.`);
}

/** v2 — validate one `needs.vector` value (engine string / bool / `{ engine, dim }`). */
function validateVectorNeed(vector: unknown, label: string): void {
  if (typeof vector === 'boolean') return;
  if (typeof vector === 'string') {
    assertVectorNeedEngine(vector, label);
    return;
  }
  if (isPlainObject(vector)) {
    checkUnknownKeys(vector, VECTOR_NEED_KEYS, label);
    const engine = vector['engine'];
    if (engine !== undefined) assertVectorNeedEngine(engine, `${label}.engine`);
    const dim = vector['dim'];
    if (dim !== undefined && (typeof dim !== 'number' || !Number.isInteger(dim) || dim <= 0)) {
      die(`'${label}.dim' must be a positive integer.`);
    }
    return;
  }
  die(`'${label}' must be an engine name (e.g. pgvector), true, or a { engine, dim } object.`);
}

/** v2 — validate one `needs.object_storage` value (`true`/`false` or `{ size }`). */
function validateObjectStorageNeed(raw: unknown, label: string): void {
  if (typeof raw === 'boolean') return;
  if (isPlainObject(raw)) {
    checkUnknownKeys(raw, OBJECT_STORAGE_NEED_KEYS, label);
    const size = raw['size'];
    if (size !== undefined && (typeof size !== 'string' || !isK8sQuantity(size))) {
      die(`'${label}.size' must be a Kubernetes quantity such as 10Gi or 100Mi.`);
    }
    return;
  }
  die(`'${label}' must be true, false, or a { size } object.`);
}

/**
 * v2 — validate one `needs.disk` value (the durable filesystem mount; rename of
 * the v1 `persist:` PVC). Accepts `true`/`false` (grid default 50MB @ `/data`)
 * or `{ size, mount }`. `size` is a positive Kubernetes Quantity; `mount` is an
 * absolute, non-system path (defaults to `/data`).
 */
function validateDiskNeed(raw: unknown, label: string): void {
  if (typeof raw === 'boolean') return;
  if (!isPlainObject(raw)) {
    die(`'${label}' must be true, false, or a { size, mount } object.`);
  }
  checkUnknownKeys(raw, DISK_NEED_KEYS, label);
  const size = raw['size'];
  if (size !== undefined && (typeof size !== 'string' || !isK8sQuantity(size))) {
    die(`'${label}.size' must be a Kubernetes quantity such as 1Gi or 500Mi.`);
  }
  const mount = raw['mount'];
  if (mount !== undefined) {
    if (typeof mount !== 'string' || !mount.startsWith('/')) {
      die(`'${label}.mount' must be an absolute path starting with '/'.`);
    }
    if (isForbiddenPersistPath(mount)) {
      die(
        `'${label}.mount' uses a system path '${mount}'. ` +
          'Choose a path under your app directory, e.g. /data.',
      );
    }
  }
}

/**
 * v2 (founder-finalized) — validate one `needs:` block (top-level or
 * per-service). Dies on unknown keys, malformed substrate values, or non-boolean
 * `ai`. The hybrid merge (Q8) happens separately in {@link mergeNeeds}; this
 * validates each block on its own so a typo surfaces at its own path.
 */
export function validateNeedsBlock(needs: unknown, label: string): void {
  if (!isPlainObject(needs)) {
    die(
      `'${label}' must be a map of capabilities ` +
        '(database, cache, kv, queue, pubsub, vector, object_storage, disk, ai).',
    );
  }
  checkUnknownKeys(needs, NEEDS_KEYS, label);

  if (needs['database'] !== undefined) validateDatabaseNeed(needs['database'], `${label}.database`);
  for (const key of REDIS_NEED_KEYS_SET) {
    if (needs[key] !== undefined) validateRedisNeed(needs[key], `${label}.${key}`);
  }
  if (needs['vector'] !== undefined) validateVectorNeed(needs['vector'], `${label}.vector`);
  if (needs['object_storage'] !== undefined) {
    validateObjectStorageNeed(needs['object_storage'], `${label}.object_storage`);
  }
  if (needs['disk'] !== undefined) validateDiskNeed(needs['disk'], `${label}.disk`);

  const ai = needs['ai'];
  if (ai !== undefined && typeof ai !== 'boolean') {
    die(`'${label}.ai' must be true or false (got ${JSON.stringify(ai)}).`);
  }
}

/**
 * v2 (founder Q8) — merge a service-level `needs:` over the app-level default.
 * The service overrides the app for any key it declares and inherits the rest
 * (a shallow, per-need-key merge). Returns a new object; inputs are not
 * mutated. With no service override the app block is returned verbatim (copied);
 * with no app default the service block stands alone.
 */
export function mergeNeeds(
  appNeeds: NeedsBlock | undefined,
  svcNeeds: NeedsBlock | undefined,
): NeedsBlock {
  return { ...(appNeeds ?? {}), ...(svcNeeds ?? {}) };
}

/** v2 — is a need value active (declared and not opted out with `false`)? */
function isNeedActive(value: unknown): boolean {
  return value !== undefined && value !== false;
}

/**
 * v2 — translate a `needs:` block back onto the v1 `requires:` wire so the
 * existing parser/deployer path (parseRequires → ParsedRequires) is reused
 * unchanged. The substrate needs map across:
 *   - `database` → `db` / `{db: private}` / `{db: external, secret}`
 *   - the four Redis-backed needs (`cache`/`kv`/`queue`/`pubsub`) → ONE `redis`
 *     entry (they share one physical Redis); `engine: external` on any of them
 *     promotes to `{redis: external, secret}`
 *   - `vector` → `vector` / `{vector: { engine }}`
 * The capability needs (`object_storage`, `disk`, `ai`) have no `requires:`
 * equivalent and are carried separately by {@link parseNeeds}.
 */
export function needsToRequires(needs: NeedsBlock): NonNullable<CloudGridYaml['requires']> {
  const out: NonNullable<CloudGridYaml['requires']> = [];

  const db = needs.database;
  if (isNeedActive(db)) {
    // `true` → grid decides; on the wire the pool keyword (`db`) is the default —
    // Decision 035 resolves the org's actual tier at deploy time.
    const tier = db === true ? 'pool' : typeof db === 'string' ? db : (db as DatabaseNeed).tier;
    if (tier === 'pool') out.push('db');
    else if (tier === 'dedicated') out.push({ db: 'private' });
    else if (tier === 'external') {
      const secret = typeof db === 'object' && db !== null ? ((db as DatabaseNeed).secret ?? '') : '';
      out.push({ db: 'external', secret });
    }
  }

  // The four Redis-backed needs collapse to one Redis on the wire. If any pins
  // `engine: external`, the whole entity's Redis is the BYOC one.
  let redisShared = false;
  let redisExternalSecret: string | undefined;
  for (const key of REDIS_NEED_KEYS_SET) {
    const v = needs[key];
    if (!isNeedActive(v)) continue;
    if (v === true || v === 'redis') redisShared = true;
    else if (typeof v === 'object' && v !== null) {
      if (v.engine === 'external') redisExternalSecret = v.secret ?? '';
      else redisShared = true;
    }
  }
  if (redisExternalSecret !== undefined) out.push({ redis: 'external', secret: redisExternalSecret });
  else if (redisShared) out.push('redis');

  const vector = needs.vector;
  if (isNeedActive(vector)) {
    if (vector === true) out.push('vector');
    else if (typeof vector === 'string') out.push({ vector });
    else if (typeof vector === 'object' && vector !== null) {
      out.push(vector.engine ? { vector: { engine: vector.engine } } : 'vector');
    }
  }

  return out;
}

/**
 * v2 — the accept-both conceptual mapping from a v1 `requires:` list to the v2
 * `needs:` vocabulary, for documentation + the deprecation surface. The deployer
 * still reads `requires:` directly during the transition window (so this is not
 * on the deploy hot path); it exists so the contract — `requires: [redis]` is
 * `cache + queue`, `[db]`/`[mongodb]` is `database`, `[vector]` is
 * `vector: pgvector` — is testable and documented in one place.
 */
export function requiresToNeeds(
  requires: CloudGridYaml['requires'],
): NeedsBlock {
  const needs: NeedsBlock = {};
  if (!requires) return needs;
  for (const entry of requires) {
    if (typeof entry === 'string') {
      if (entry === 'redis') {
        // The most common Redis pattern is a cache plus a job queue.
        needs.cache = true;
        needs.queue = true;
      } else if (entry === 'db' || entry === 'mongodb') {
        needs.database = true;
      } else if (entry === VECTOR_REQUIRES_KEYWORD) {
        needs.vector = 'pgvector';
      }
    } else if (isPlainObject(entry)) {
      if ('redis' in entry) {
        needs.cache = true;
        needs.queue = true;
      }
      if ('db' in entry || 'mongodb' in entry) {
        needs.database = true;
      }
      if (VECTOR_REQUIRES_KEYWORD in entry) {
        const v = (entry as Record<string, unknown>)[VECTOR_REQUIRES_KEYWORD];
        needs.vector =
          typeof v === 'object' && v !== null && typeof (v as VectorNeed).engine === 'string'
            ? ((v as VectorNeed).engine as string)
            : 'pgvector';
      }
    }
  }
  return needs;
}

/**
 * v2 — normalize a `needs:` block into {@link ParsedNeeds}. The substrate
 * fields are produced by translating to the v1 wire and running the existing
 * {@link parseRequires} (so DB-tier conflicts, unknown engines, and shared-
 * service lookups are validated identically); the capability flags are recorded
 * directly. Pass the (already merged) effective needs for a service.
 */
export function parseNeeds(needs: NeedsBlock, registry: SharedServicesRegistry): ParsedNeeds {
  const requires = needsToRequires(needs);
  const parsed = parseRequires(requires, registry) as ParsedNeeds;
  if (isNeedActive(needs.cache)) parsed.cache = true;
  if (isNeedActive(needs.kv)) parsed.kv = true;
  if (isNeedActive(needs.queue)) parsed.queue = true;
  if (isNeedActive(needs.pubsub)) parsed.pubsub = true;
  if (isNeedActive(needs.object_storage)) parsed.objectStorage = true;
  if (isNeedActive(needs.disk)) parsed.disk = needs.disk;
  if (needs.ai) parsed.ai = true;
  return parsed;
}

/**
 * v2 (founder-finalized) — accept-both orchestration. Detects whether the yaml
 * uses the v1 (`requires:` / `persist:`) or v2 (`needs:`) vocabulary, rejects
 * mixing the two, fires a deprecation event for v1 fields, and validates every
 * v2 `needs:` block (top-level + per-service). The durable disk now lives at
 * `needs.disk`; the v1 `persist:` maps to it.
 *
 * Runs once, early in {@link validateConfig} (after the top-level strict-keys
 * pass, before the per-service loop) so a vocab-mix or a needs typo surfaces
 * before the existing v1 requires/persist validation runs.
 */
function validateSchemaShapes(config: CloudGridYaml, opts: ValidateOptions): void {
  const services = isPlainObject(config.services) ? config.services : {};

  const topNeeds = (config as { needs?: unknown }).needs;
  const serviceNeeds = Object.entries(services).filter(
    ([, svc]) => isPlainObject(svc) && (svc as { needs?: unknown }).needs !== undefined,
  );
  const hasNeeds = topNeeds !== undefined || serviceNeeds.length > 0;

  const hasRequires = (config as { requires?: unknown }).requires !== undefined;
  const hasPersist =
    (config as { persist?: unknown }).persist !== undefined ||
    Object.values(services).some(
      (svc) => isPlainObject(svc) && (svc as { persist?: unknown }).persist !== undefined,
    );

  // ── needs (v2) ↔ requires/persist (v1): pick one vocabulary ──
  if (hasNeeds && hasRequires) {
    die(
      "Both 'needs:' and 'requires:' are set in cloudgrid.yaml. Use one or " +
        "the other — 'requires:' is the deprecated v1 shape; 'needs:' is the " +
        'replacement. Remove the requires: block.',
    );
  }
  if (hasNeeds && hasPersist) {
    die(
      "Both 'needs:' and 'persist:' are set in cloudgrid.yaml. The durable disk " +
        "now lives at 'needs.disk:' — 'persist:' is the deprecated v1 name. " +
        'Move it under needs: and remove the persist: block.',
    );
  }

  if (hasRequires) fireDeprecation(opts, 'requires', 'needs');
  if (hasPersist) fireDeprecation(opts, 'persist', 'needs.disk');

  if (topNeeds !== undefined) validateNeedsBlock(topNeeds, 'needs');
  for (const [svcName, svc] of serviceNeeds) {
    validateNeedsBlock((svc as { needs?: unknown }).needs, `services.${svcName}.needs`);
  }
}

/**
 * BL628 — validate the top-level `scale:` declaration. Accepts the two intent
 * keywords (`auto` / `alert`) or an integer in [MIN_SCALE, MAX_SCALE]. Anything
 * else dies with a §23-voice message; a close keyword typo lands a "Did you
 * mean" hint. When the value is `alert` (a deliberate single-replica), the SPOF
 * advisory is surfaced through the CLI warn channel so the developer sees it at
 * plug time — server-side callers leave `warn` undefined, so platform logs are
 * not spammed. The effective replica count itself is resolved server-side at
 * deploy time (see `resolveScale`); this only gates the SHAPE of the field.
 */
function validateScale(config: CloudGridYaml, opts: ValidateOptions): void {
  const raw = (config as { scale?: unknown }).scale;
  if (raw === undefined) return;
  const rangeMsg =
    `'scale' must be 'auto', 'alert', or an integer between ${MIN_SCALE} and ` +
    `${MAX_SCALE} (got ${JSON.stringify(raw)}).`;
  if (typeof raw === 'string') {
    if (raw !== 'auto' && raw !== 'alert') {
      const hint = didYouMeanLine(raw, ['auto', 'alert']);
      die(rangeMsg + (hint ? ` ${hint}` : ''));
    }
    // `scale: alert` — the owner opts into single-replica on purpose. Nudge them
    // (CLI only) that this is a SPOF so it's a conscious choice, not an oversight.
    if (raw === 'alert' && opts.warn) opts.warn(SPOF_WARNING);
    return;
  }
  if (typeof raw === 'number') {
    if (!Number.isInteger(raw) || raw < MIN_SCALE || raw > MAX_SCALE) {
      die(rangeMsg);
    }
    return;
  }
  die(rangeMsg);
}

export function validateConfig(
  config: CloudGridYaml,
  opts: ValidateOptions = {},
): void {
  // Decision 012: cloudgrid.yaml is purely service-shape; identity lives in
  // .cloudgrid/link.json. Reject the legacy `org:` field so users see a clear
  // pointer instead of silently ignoring a stale pin.
  //
  // Run this BEFORE validateName so a yaml carrying both an old `org:` AND a
  // name typo surfaces the migration error first — that's the more actionable
  // fix for v0.7 → v0.8 migrators.
  if ((config as any).org !== undefined) {
    die(
      "'org:' in cloudgrid.yaml is no longer supported. " +
      'Identity now lives in .cloudgrid/link.json. ' +
      'Remove this line from the yaml.',
    );
  }

  // Backlog 191 — `network.public` → `expose` migration. The parser accepts
  // both forms during the deprecation window. When only the legacy form is
  // present we copy its value onto `expose` and warn. When both forms are
  // present and disagree, we die — silently picking one would mask a real
  // intent mismatch in the yaml. When both forms agree, we warn but keep
  // the new form.
  //
  // The yaml convention here is `expose: false` as the explicit internal-only
  // sentinel; `expose: true` (or absent) means "serves HTTP", which is the
  // default. The legacy `network.public` carries the same semantics.
  const rawExpose = (config as { expose?: unknown }).expose;
  const rawNetwork = config.network;
  const legacyPublic = rawNetwork?.public;
  if (rawExpose !== undefined && typeof rawExpose !== 'boolean') {
    die(`'expose' must be true or false (got ${JSON.stringify(rawExpose)})`);
  }
  if (legacyPublic !== undefined && typeof legacyPublic !== 'boolean') {
    die(`'network.public' must be true or false (got ${JSON.stringify(legacyPublic)})`);
  }
  if (rawExpose !== undefined && legacyPublic !== undefined && rawNetwork) {
    if (rawExpose !== legacyPublic) {
      die(formatExposeConflictError(rawExpose, legacyPublic));
    }
    if (opts.warn) opts.warn(formatNetworkPublicDeprecatedWarning());
    // Both agree; drop the legacy field so downstream consumers see one
    // canonical source.
    delete rawNetwork.public;
  } else if (legacyPublic !== undefined && rawNetwork) {
    (config as { expose?: boolean }).expose = legacyPublic;
    if (opts.warn) opts.warn(formatNetworkPublicDeprecatedWarning());
    delete rawNetwork.public;
  }

  // Backlog 212 phase 2 — strict-keys check (ERROR mode). Runs AFTER the
  // legacy-field migrations above so the deleted `network.public` /
  // `domain:` keys do not trigger false-positive rejections. Phase 1
  // (0.8.14) shipped this as a non-blocking warning with a one-release
  // deprecation window; phase 2 (0.8.15) flips to die() so silently-
  // ignored typos can no longer slip through. Each die() includes a
  // Levenshtein-1 fuzzy-match suggestion when close and the full set of
  // known fields at that nesting level.
  //
  // Fires unconditionally — CLI preflight and server-side callers both
  // reject typos now. Deprecated fields stay accepted via the WARN
  // channel above (network.public, domain); only TRULY unknown fields
  // hit this pass.
  checkUnknownKeys(
    config as unknown as Record<string, unknown>,
    TOP_LEVEL_KEYS,
    'cloudgrid.yaml (top level)',
  );

  // cloudgrid.yaml v2 (founder-finalized vocabulary, 2026-06-25) — accept-both
  // orchestration: reject needs (v2) ↔ requires/persist (v1) vocab mixing, fire
  // a deprecation event for v1 fields, validate every v2 needs: block. The
  // durable disk lives at needs.disk; the v1 persist: maps to it. Runs after the
  // top-level strict-keys pass (so the fields are known) and before the v1
  // requires forEach + per-service loop (so a vocab-mix surfaces first).
  validateSchemaShapes(config, opts);
  // BL628 — validate `scale:` shape (and surface the SPOF nudge for `alert`).
  // Runs after the top-level strict-keys pass (so the field is known) and is
  // independent of the substrate validation around it.
  validateScale(config, opts);
  const rawNetworkObj = (config as { network?: unknown }).network;
  if (isPlainObject(rawNetworkObj)) {
    checkUnknownKeys(rawNetworkObj, NETWORK_KEYS, 'network');
  }
  const rawProbeObj = (config as { probe?: unknown }).probe;
  if (isPlainObject(rawProbeObj)) {
    checkUnknownKeys(rawProbeObj, PROBE_KEYS, 'probe');
  }
  // Backlog 192 phase 2 — strict-keys on `callers:` sub-keys. Runs before
  // the deeper validation below so a typo (e.g. `polciy:`) produces the
  // Levenshtein-1 suggestion instead of "policy must be auto|manual".
  const rawCallersObj = (config as { callers?: unknown }).callers;
  if (isPlainObject(rawCallersObj)) {
    checkUnknownKeys(rawCallersObj, CALLERS_KEYS, 'callers');
  }
  // Dev Grid §M0 — `dev:` block. Strict-keys at both levels, then validate the
  // one field v1 reads: `dev.snapshot.mask` as a list of `collection.field`
  // dotted paths.
  const rawDevObj = (config as { dev?: unknown }).dev;
  if (isPlainObject(rawDevObj)) {
    checkUnknownKeys(rawDevObj, DEV_KEYS, 'dev');
    // Dev Grid §M1 — `dev.schema` is the authoritative seed schema: a map of
    // collection name → flat array of field names.
    const rawDevSchema = (rawDevObj as { schema?: unknown }).schema;
    if (rawDevSchema !== undefined) {
      if (!isPlainObject(rawDevSchema)) {
        die(`'dev.schema' must be a map of 'collection: [field, ...]' (got ${JSON.stringify(rawDevSchema)})`);
      }
      for (const [collection, fields] of Object.entries(rawDevSchema as Record<string, unknown>)) {
        if (!DEV_SCHEMA_NAME_REGEX.test(collection)) {
          die(
            `Invalid 'dev.schema' collection name ${JSON.stringify(collection)}.\n` +
            `  Collection names are lowercase letters, digits, and underscores,\n` +
            `  starting with a letter (e.g. 'users').`,
          );
        }
        if (!Array.isArray(fields) || fields.length === 0) {
          die(`'dev.schema.${collection}' must be a non-empty list of field names.`);
        }
        fields.forEach((field, idx) => {
          if (typeof field !== 'string' || !DEV_SCHEMA_NAME_REGEX.test(field)) {
            die(
              `Invalid 'dev.schema.${collection}[${idx}]' value: ${JSON.stringify(field)}.\n` +
              `  Each field name is lowercase letters, digits, and underscores,\n` +
              `  starting with a letter (e.g. 'email'). Nested fields are not supported yet.`,
            );
          }
        });
      }
    }
    const rawDevSnapshot = (rawDevObj as { snapshot?: unknown }).snapshot;
    if (isPlainObject(rawDevSnapshot)) {
      checkUnknownKeys(rawDevSnapshot, DEV_SNAPSHOT_KEYS, 'dev.snapshot');
      const rawMask = (rawDevSnapshot as { mask?: unknown }).mask;
      if (rawMask !== undefined) {
        if (!Array.isArray(rawMask)) {
          die(`'dev.snapshot.mask' must be a list of 'collection.field' paths (got ${JSON.stringify(rawMask)})`);
        }
        rawMask.forEach((entry, idx) => {
          if (typeof entry !== 'string' || !MASK_ENTRY_REGEX.test(entry)) {
            die(
              `Invalid 'dev.snapshot.mask[${idx}]' value: ${JSON.stringify(entry)}.\n` +
              `  Each entry is a 'collection.field' path — one dot, lowercase\n` +
              `  letters, digits, and underscores, each side starting with a letter\n` +
              `  (e.g. 'users.email'). Nested-field masking is not supported yet.`,
            );
          }
        });
      }
    }
  }
  const rawRequires = (config as { requires?: unknown }).requires;
  if (Array.isArray(rawRequires)) {
    rawRequires.forEach((entry, idx) => {
      if (!isPlainObject(entry)) return;
      // Two valid object-form shapes:
      //   - `{ <name>: 'private' }` — private resource modifier, gated to
      //     the W1.C shorthand keys (db / redis / mongodb — the latter so
      //     the dedicated MONGODB_PRIVATE_DEPRECATED die fires below
      //     instead of a generic unknown-field error)
      //   - `{ gcp: { services: [...] } }` — gcp services block
      // Anything else passes through with `gcp` as the only known key.
      const entryKeys = Object.keys(entry);
      const isPrivateShorthand =
        entryKeys.length === 1 &&
        entry[entryKeys[0]] === 'private' &&
        REQUIRES_PRIVATE_SHORTHAND_KEYS.has(entryKeys[0]);
      if (isPrivateShorthand) return;
      // DB W1.C (spec §2.1) — the two multi-key shapes get their own
      // known-key sets so a typo (`secrt:`, `prot:`) lands a Levenshtein
      // suggestion instead of the generic gcp-only message. Semantic
      // validation (modes, ports, vault cross-check) runs below in
      // validateDbRequires.
      if ('external_db' in entry) {
        checkUnknownKeys(entry, REQUIRES_NATIVE_EGRESS_KEYS, `requires[${idx}]`);
        return;
      }
      if (entry['db'] === 'external' || entry['redis'] === 'external') {
        checkUnknownKeys(entry, REQUIRES_EXTERNAL_ITEM_KEYS, `requires[${idx}]`);
        return;
      }
      // BL579 M2 — `{ vector: ... }` (engine pin). Its own known-key set so a
      // sibling typo lands a suggestion instead of the generic gcp-only message.
      // Engine validation (known engine, shape) runs in validateVectorRequires.
      if (VECTOR_REQUIRES_KEYWORD in entry) {
        checkUnknownKeys(entry, REQUIRES_VECTOR_ITEM_KEYS, `requires[${idx}]`);
        return;
      }
      checkUnknownKeys(entry, REQUIRES_ITEM_KEYS, `requires[${idx}]`);
    });
  }

  // Backlog 104 — legacy 'domain:' field migration. One release window of
  // dual support; after that, the field is removed from the schema.
  const legacyDomain = (config as { domain?: unknown }).domain;
  const hasCustomDomains = Array.isArray((config as { custom_domains?: unknown }).custom_domains);
  if (legacyDomain !== undefined && hasCustomDomains) {
    die(
      `Both 'domain' and 'custom_domains' are set in cloudgrid.yaml. ` +
      `'domain' is deprecated — use 'custom_domains' only.`,
    );
  }
  if (typeof legacyDomain === 'string') {
    if (legacyDomain.endsWith('.apps.cloudgrid.io')) {
      // Legacy apps wildcard — drop. Entity now serves on its org-default URL only.
      if (opts.warn) opts.warn(formatLegacyAppsDomainWarning(legacyDomain));
    } else {
      // True custom — translate into custom_domains.
      (config as { custom_domains?: string[] }).custom_domains = [legacyDomain];
      if (opts.warn) opts.warn(formatDomainDeprecatedWarning(legacyDomain));
    }
    delete (config as { domain?: unknown }).domain;
  }

  validateName(config.name, 'app name');

  if (!config.services || Object.keys(config.services).length === 0) {
    die('No services defined in cloudgrid.yaml');
  }

  // Backlog 021 — optional `notify: false` opt-out for org-default deploy-failure
  // notifications. Only `true | false` accepted; anything else fails fast so a
  // typo can't silently turn off pings.
  if (config.notify !== undefined && typeof config.notify !== 'boolean') {
    die(`'notify' must be true or false (got ${JSON.stringify(config.notify)})`);
  }

  // The routability check (backlog 082 / 092) lives AFTER the per-service
  // loop — it uses the `paths` Set populated below. Running it here would
  // mask per-service errors like `path: true` (which fails the loop's
  // type-check but doesn't add to the Set, so the routability check would
  // fire first with a misleading message).

  const paths = new Set<string>();
  for (const [name, svc] of Object.entries(config.services)) {
    validateName(name, 'service name');

    // Backlog 473 (first slice) — validate `persist:` (and its single-replica
    // gate) BEFORE the strict-keys check below. A `persist:` + `replicas > 1`
    // declaration must surface the migration message naming `requires: [db]`,
    // not the generic "unknown field 'replicas'" strict-keys error.
    validatePersist(name, svc);

    // Backlog 212 phase 2 — per-service strict-keys ERROR. Run before the
    // shape checks below so a typo at the service level (e.g. `node_versionn:`)
    // surfaces a suggestion before any later shape check has a chance to
    // produce a less helpful error against the same field.
    if (isPlainObject(svc)) {
      checkUnknownKeys(svc, SERVICE_KEYS, `services.${name}`);
      const svcBuild = (svc as { build?: unknown }).build;
      if (isPlainObject(svcBuild)) {
        checkUnknownKeys(svcBuild, SERVICE_BUILD_KEYS, `services.${name}.build`);
      }
    }

    if (!VALID_TYPES.includes(svc.type as any)) {
      const hint = didYouMeanLine(String(svc.type), VALID_TYPES);
      die(
        `Service '${name}' has invalid type '${svc.type}'. Must be ${VALID_TYPES.join('|')}.` +
        (hint ? ` ${hint}` : ''),
      );
    }

    if (svc.lang !== undefined) {
      if (svc.lang !== 'javascript' && svc.lang !== 'typescript') {
        die(`Service '${name}' has invalid lang '${svc.lang}'. Must be javascript|typescript`);
      }
      if (svc.type !== 'node') {
        die(`Service '${name}' uses lang but type is '${svc.type}'. lang is only valid for type: node`);
      }
    }

    // Backlog 124 — optional `source:` block. Today's behavior (no source =
    // pack `services/<svc>/`) is the no-op default. When declared, `source`
    // must be an object; the only field accepted in this release is `path`,
    // a relative directory under the project root. Future fields
    // (`source.git`, `source.subpath`, etc.) require a schema bump and are
    // rejected here so a typo doesn't silently change deploy behavior.
    const rawSource = (svc as { source?: unknown }).source;
    if (rawSource !== undefined) {
      if (
        typeof rawSource !== 'object' ||
        rawSource === null ||
        Array.isArray(rawSource)
      ) {
        die(formatSourceShapeError(name));
      }
      const sourceCfg = rawSource as Record<string, unknown>;
      for (const key of Object.keys(sourceCfg)) {
        if (key !== 'path') {
          die(formatSourceUnsupportedKeyError(name, key));
        }
      }
      if (sourceCfg.path !== undefined) {
        if (typeof sourceCfg.path !== 'string' || !isValidSourcePath(sourceCfg.path)) {
          die(formatSourcePathError(name, sourceCfg.path));
        }
      }
    }

    // Backlog 108 — optional `build:` block for `type: static` services. Runs
    // a build command server-side inside the service folder before bundling
    // the static layer. Only valid for `type: static`. Mirrors the lang-check
    // shape above so the rejection messaging is consistent.
    const rawBuild = (svc as { build?: unknown }).build;
    if (rawBuild !== undefined) {
      if (svc.type !== 'static') {
        die(formatBuildTypeError(name, svc.type));
      }
      if (typeof rawBuild !== 'object' || rawBuild === null || Array.isArray(rawBuild)) {
        die(formatBuildShapeError(name));
      }
      const buildCfg = rawBuild as { command?: unknown; output?: unknown };
      if (typeof buildCfg.command !== 'string' || buildCfg.command.trim().length === 0) {
        die(formatBuildCommandError(name));
      }
      if (buildCfg.output !== undefined) {
        if (typeof buildCfg.output !== 'string' || !isValidBuildOutput(buildCfg.output)) {
          die(formatBuildOutputError(name, buildCfg.output));
        }
      }
      // Backlog 207 part 3 — optional `build.env:` map. Build-time env vars
      // exported into the static-build Cloud Build step before `command`
      // runs (e.g. `VITE_API_URL`, `NEXT_PUBLIC_*` for static export, build-
      // time feature flags). DIFFERENT from runtime `services.<svc>.env:` —
      // those land in the pod ConfigMap; these only exist while `npm run
      // build` runs in the builder image. Reserved-key check mirrors the
      // runtime reserved-keys set so a user cannot override platform vars
      // like PORT, APP_NAME, _URL suffix, or the CLOUDGRID_* namespace.
      const rawBuildEnv = (rawBuild as { env?: unknown }).env;
      if (rawBuildEnv !== undefined) {
        if (
          typeof rawBuildEnv !== 'object' ||
          rawBuildEnv === null ||
          Array.isArray(rawBuildEnv)
        ) {
          die(formatBuildEnvShapeError(name));
        }
        for (const [envKey, envVal] of Object.entries(rawBuildEnv as Record<string, unknown>)) {
          if (typeof envVal !== 'string') {
            die(formatBuildEnvValueError(name, envKey, envVal));
          }
          if (!BUILD_ENV_NAME_PATTERN.test(envKey)) {
            die(formatBuildEnvNameError(name, envKey));
          }
          if (isReservedBuildEnvKey(envKey)) {
            die(formatBuildEnvReservedError(name, envKey));
          }
        }
      }
    }

    // Backlog 207 — optional `node_version:` override for the static-build
    // step image. Only `type: static` consumes this today (the only build
    // path that picks a node image); other types use language-specific
    // Dockerfile templates and would silently ignore the field, so we reject
    // it up front rather than let it look honored.
    const rawNodeVersion = (svc as { node_version?: unknown }).node_version;
    if (rawNodeVersion !== undefined) {
      if (svc.type !== 'static') {
        die(formatNodeVersionTypeError(name, svc.type));
      }
      if (
        typeof rawNodeVersion !== 'string' ||
        !VALID_NODE_VERSIONS.includes(rawNodeVersion as (typeof VALID_NODE_VERSIONS)[number])
      ) {
        die(formatNodeVersionValueError(name, rawNodeVersion));
      }
    }

    // Backlog 541 — optional `base_image:` opt-out for the `type: nextjs`
    // template v2 final stage. Only the Next.js template branches on it (the
    // distroless-vs-alpine final stage); every other type builds from a
    // template that pins its own runtime, so reject the field there rather
    // than let it look honored. Values are restricted to the two the renderer
    // understands.
    const rawBaseImage = (svc as { base_image?: unknown }).base_image;
    if (rawBaseImage !== undefined) {
      if (svc.type !== 'nextjs') {
        die(formatBaseImageTypeError(name, svc.type));
      }
      if (rawBaseImage !== 'alpine' && rawBaseImage !== 'distroless') {
        die(formatBaseImageValueError(name, rawBaseImage));
      }
    }

    // Backlog 207 — optional `port:` override for the container's listener.
    // Cron services have no HTTP surface and the renderer never emits a
    // containerPort / Service for them, so reject early instead of letting
    // a user think the override is honored. For every other type the value
    // must be a positive integer in the TCP port range; non-integers and
    // out-of-range numbers are rejected so a typo doesn't reach the
    // manifest and surface as an opaque kubelet error.
    const rawPort = (svc as { port?: unknown }).port;
    if (rawPort !== undefined) {
      if (svc.type === 'cron') {
        die(formatPortTypeError(name));
      }
      if (
        typeof rawPort !== 'number' ||
        !Number.isInteger(rawPort) ||
        rawPort < 1 ||
        rawPort > 65535
      ) {
        die(formatPortValueError(name, rawPort));
      }
    }

    // Backlog 092 — normalise `path:` before validation.
    //   yaml `null` / `~`     → JS null   → treated as `false` (yaml convention)
    //   yaml `false`          → JS false  → explicit internal-only
    //   yaml `true`           → JS true   → reject (no inverse)
    //   yaml `'/api'`         → JS string → public, validated below
    //   field absent          → JS undefined → renderer-no-Ingress; soft warning below
    const rawPath = (svc as { path?: unknown }).path;
    let normalisedPath: string | false | undefined;
    if (rawPath === undefined) {
      normalisedPath = undefined;
    } else if (rawPath === null || rawPath === false) {
      normalisedPath = false;
    } else if (rawPath === true) {
      die(
        `Service '${name}' has 'path: true', which is not valid. ` +
          `Use path: '/' for public root, or path: false for internal-only.`,
      );
    } else if (typeof rawPath === 'string') {
      if (!rawPath.startsWith('/')) {
        die(`Service '${name}' path '${rawPath}' must start with /`);
      }
      if (paths.has(rawPath)) {
        die(`Duplicate path '${rawPath}'`);
      }
      paths.add(rawPath);
      normalisedPath = rawPath;
    } else {
      die(
        `Service '${name}' has invalid path value: ${JSON.stringify(rawPath)}. ` +
          `Expected a string starting with '/', 'false' for internal-only, or omit the field.`,
      );
    }

    // Backlog 101 — opt-in `health:` for stricter HTTP probe. Three valid
    // shapes: absent / `false` (TCP, the 098 default), `true` (sentinel for
    // `/health`), or a string starting with `/` (HTTP probe at that path).
    // BL572 adds a fourth: the object form `{ path, expected }` (the
    // application-HTTP-health override the recovery reconciler reads).
    // Anything else is rejected with a §23-voiced message.
    const rawHealth = (svc as { health?: unknown }).health;
    if (rawHealth !== undefined && rawHealth !== false && rawHealth !== true) {
      if (isPlainObject(rawHealth)) {
        // BL572 object form. Strict-keys first so a typo (`pat:`) gets the
        // Levenshtein suggestion before any value check.
        checkUnknownKeys(rawHealth, HEALTH_KEYS, `services.${name}.health`);
        const hPath = (rawHealth as { path?: unknown }).path;
        if (hPath !== undefined) {
          if (typeof hPath !== 'string' || !hPath.startsWith('/')) {
            die(formatHealthInvalidError(name, rawHealth));
          }
        }
        const hExpected = (rawHealth as { expected?: unknown }).expected;
        if (hExpected !== undefined) {
          // YAML parses a bare `200` as a number; coerce before matching.
          const expectedStr =
            typeof hExpected === 'number' ? String(hExpected) : hExpected;
          if (typeof expectedStr !== 'string' || !HEALTH_EXPECTED_REGEX.test(expectedStr)) {
            die(
              `Service '${name}' has an invalid 'health.expected' value: ${JSON.stringify(hExpected)}.\n` +
                `  Expected one of: 'healthy' (default), a 3-digit code like '200', a class\n` +
                `  like '2xx', or '<500'.`,
            );
          }
        }
      } else if (typeof rawHealth !== 'string' || !rawHealth.startsWith('/')) {
        die(formatHealthInvalidError(name, rawHealth));
      }
    }

    // Cron-specific validation
    if (svc.type === 'cron') {
      if (!svc.schedule) {
        die(`Service '${name}' is type 'cron' but missing required 'schedule' field`);
      }
      // Backlog 092 — cron + path: false is accepted as a no-op (lets a
      // developer use the same explicit-internal sentinel everywhere).
      // Cron + string path is still rejected (cron services have no
      // HTTP surface to route).
      if (typeof normalisedPath === 'string') {
        die(`Service '${name}' is type 'cron' and must not have a 'path' field`);
      }
      if (svc.timezone !== undefined && !VALID_TIMEZONES.includes(svc.timezone as any)) {
        die(`Service '${name}' has invalid timezone '${svc.timezone}'. Must be ${VALID_TIMEZONES.join('|')}`);
      }
      if (svc.run !== undefined && svc.run !== 'job' && !svc.run.startsWith('http://') && !svc.run.startsWith('https://')) {
        die(`Service '${name}' has invalid run '${svc.run}'. Must be 'job', 'http://...', or 'https://...'`);
      }
    } else if (
      // Backlog 092 — soft warning for kind=app non-cron services where
      // `path:` is absent. The yaml still validates; we just suggest the
      // explicit `path: false` form so the trace can name the choice.
      opts.kind === 'app' &&
      normalisedPath === undefined &&
      opts.warn
    ) {
      opts.warn(formatPathAbsentWarning(name));
    }

    // Backlog 189 Level 1 — per-service `depends_on:` shape check. Cross-service
    // reference validity is enforced after the loop (once all service names
    // are known); here we only reject malformed shape so the cross-ref pass
    // can assume a string[].
    const rawDependsOn = (svc as { depends_on?: unknown }).depends_on;
    if (rawDependsOn !== undefined) {
      if (!Array.isArray(rawDependsOn)) {
        die(formatDependsOnShapeError(name));
      }
      const seen = new Set<string>();
      for (const dep of rawDependsOn) {
        if (typeof dep !== 'string') {
          die(formatDependsOnShapeError(name));
        }
        if (dep === name) {
          die(formatDependsOnSelfReferenceError(name));
        }
        if (seen.has(dep)) {
          die(formatDependsOnDuplicateError(name, dep));
        }
        seen.add(dep);
      }
    }

    // Backlog 086 — service `type:` ↔ folder content alignment. Runs only
    // when the caller provides a folder reader (CLI preflight / API
    // server-side). HTTP-mode cron services have no folder; skip them
    // before invoking the factory so callers can throw on unexpected reads.
    if (opts.serviceReader && !isHttpModeCronService(svc)) {
      const reader = opts.serviceReader(name);
      if (reader !== null) {
        const result = checkServiceContentAlignment(name, svc, reader);
        if (!result.ok) {
          die(result.message);
        }
      }
    }
  }

  // Backlog 189 Level 1 — cross-service reference check. Every depends_on
  // entry must name another service declared in the same yaml. Run after the
  // per-service loop so all service names are known. Cycles are NOT checked
  // here — they surface during the renderer's topological sort, where the
  // error can name the specific cycle path.
  //
  // Backlog 201 — `depends_on:` also accepts private-resource names declared
  // via `requires: [<name>: private]`. The renderer emits a Deployment +
  // Service for each private resource (e.g. `redis`) which is a valid TCP
  // target for the initContainer probe. Names are collected by inspection
  // here (without running parseRequires, which would die on unrelated
  // requires errors that get their own dedicated message downstream).
  const privateRequiresNames = collectPrivateRequiresNames(config.requires);
  const allServiceNames = new Set(Object.keys(config.services));
  const allDependsOnTargets = new Set<string>([...allServiceNames, ...privateRequiresNames]);
  for (const [name, svc] of Object.entries(config.services)) {
    const rawDependsOn = (svc as { depends_on?: unknown }).depends_on;
    if (!Array.isArray(rawDependsOn)) continue;
    for (const dep of rawDependsOn) {
      if (typeof dep !== 'string') continue; // already rejected above
      if (!allDependsOnTargets.has(dep)) {
        die(
          formatDependsOnUnknownTargetError(
            name,
            dep,
            Array.from(allServiceNames),
            Array.from(privateRequiresNames),
          ),
        );
      }
      // Private-requires resources have their own Deployment + Service; the
      // cron-target check below only applies to user-declared services.
      if (privateRequiresNames.has(dep)) continue;
      // Cron services have no K8s Service in this renderer — there is no
      // port to TCP-probe from an initContainer. Reject so the user gets
      // a clear message instead of a deploy that never becomes Ready.
      const depCfg = config.services[dep];
      if (depCfg.type === 'cron') {
        die(formatDependsOnCronTargetError(name, dep));
      }
    }
  }

  // Backlog 104 — custom_domains[] FQDN format + scope validation. Runs
  // after the per-service loop (so per-service errors surface first) and
  // before the routability check (so a bad custom_domains in an otherwise-
  // valid yaml gets caught before the routability message).
  const customDomains = (config as { custom_domains?: unknown }).custom_domains;
  if (customDomains !== undefined) {
    if (!Array.isArray(customDomains)) {
      die(`'custom_domains' must be a list of fully-qualified domain names`);
    }
    for (const d of customDomains) {
      if (typeof d !== 'string' || !isValidFqdn(d)) {
        die(formatCustomDomainInvalidError(String(d)));
      }
      // MVP 104 — scope to cloudgrid.io only. 016 lifts this for tenant-owned.
      // Use suffix + dot OR exact-equal to prevent cloudgrid.io.attacker.com and
      // evil-cloudgrid.io from sneaking through.
      //
      // TEMP (2026-06-02, pre-016): `ngcdigital.io` is a founder-owned tenant
      // domain (same Cloudflare account as cloudgrid.io) allowlisted ahead of
      // the general backlog-016 tenant-domain scope-lift, which lands next week.
      // Exact-match the apex + www ONLY — deliberately not `endsWith('.ngcdigital.io')`,
      // so no other subdomain is silently in-scope. Remove this clause when 016
      // ships the proper path.
      const inScope =
        d === 'cloudgrid.io' ||
        d.endsWith('.cloudgrid.io') ||
        d === 'ngcdigital.io' ||
        d === 'www.ngcdigital.io';
      if (!inScope) {
        die(formatCustomDomainOutOfScopeError(d));
      }
    }
  }

  // Backlog 192 phase 7 — `internal_urls:` is no longer accepted. The caller
  // model (`callers:` / `calls:`, Decision 027a) is the auth-bearing
  // successor; the field's stop-gap window (one WARN release in 0.8.17)
  // is closed. Any yaml that still carries `internal_urls:` — regardless
  // of shape — fails validation with a single removal-notice error that
  // points at the migration. The field type and the deployer's resolver
  // service remain in the codebase for one more release; 0.8.19 removes
  // them entirely. Pre-flight against the live entity collection on
  // 2026-05-22 confirmed zero production entities still declare the
  // field, so the flip carries no migration burden.
  const rawInternalUrls = (config as { internal_urls?: unknown }).internal_urls;
  if (rawInternalUrls !== undefined) {
    die(formatInternalUrlsRemovedError());
  }

  // Backlog 082 / 092 / 191 — `app`-kind entities with `expose !== false`
  // must have at least one service declaring a STRING `path:`. `path: false`
  // is the explicit "internal-only" sentinel and does not count as routable.
  // The `paths` Set populated above only contains string paths — empty Set
  // means no public surface. Decision 020.
  //
  // The migration shim earlier in this function normalises `network.public`
  // onto `expose`, so by this point `config.expose` is the canonical source.
  if (opts.kind === 'app') {
    const exposes = config.expose !== false;
    if (exposes && paths.size === 0) {
      die(formatRoutabilityError(config.name));
    }
  }

  // Backlog 473 (second slice) — entity-level `persist:` flag + cross-form
  // checks (verbose-at-entity-level, double-declaration, ambiguous primary).
  // Runs after the per-service loop so it sees every service's `persist:` and
  // type. The per-service `validatePersist` (inside the loop) already validated
  // each service-level value's shape.
  validateEntityPersist(config);

  // Backlog 192 phase 2 / decision 027a — `callers:` (inbound policy) and
  // `calls:` (outbound dependencies). Shape-only checks here; existence of
  // a target entity is enforced at deploy time by the caller-tokens service
  // (CALLER_TARGET_NOT_FOUND). Cross-org `calls:` are permitted at the
  // schema layer; the issueGrant service returns CROSS_ORG_NOT_SUPPORTED
  // for v1 until the co-sign flow ships.
  validateCallersBlock((config as { callers?: unknown }).callers);
  validateCallsBlock((config as { calls?: unknown }).calls);

  // Decision 023 Update 2026-05-18 — Vault module 3. Validate the optional
  // `vault:` block. Real per-org item lookup + grant checks happen at plug
  // time in the deployer; here we only enforce shape so the deploy doesn't
  // run with a malformed map.
  const rawVault = (config as { vault?: unknown }).vault;
  if (rawVault !== undefined) {
    if (typeof rawVault !== 'object' || rawVault === null || Array.isArray(rawVault)) {
      die(formatVaultShapeError());
    }
    const entries = Object.entries(rawVault as Record<string, unknown>);
    for (const [envName, vaultKey] of entries) {
      if (!VAULT_ENV_NAME_PATTERN.test(envName) || envName.length > VAULT_ENV_NAME_MAX_LEN) {
        die(formatVaultEnvNameError(envName));
      }
      if (typeof vaultKey !== 'string') {
        die(formatVaultItemKeyError(envName, vaultKey));
      }
      if (!VAULT_ITEM_KEY_PATTERN.test(vaultKey) || vaultKey.length > VAULT_ITEM_KEY_MAX_LEN) {
        die(formatVaultItemKeyError(envName, vaultKey));
      }
    }
  }

  // DB W1.C (spec 2026-06-06 §2.2–2.3) — DB-tier requires shapes at CLI
  // preflight: rejects the historical `{mongodb: private}` form
  // (MONGODB_PRIVATE_DEPRECATED), per-kind mode conflicts, and bad native
  // egress ports. parseRequires re-runs the same analysis on the
  // generator/deployer path; the `secret:` ↔ `vault:` cross-check lives
  // ONLY here because validateConfig is the layer that sees the whole
  // yaml. Runs after the vault shape check above so a malformed vault
  // surfaces its own dedicated error first.
  const dbShapes = validateDbRequires(config.requires);
  // BL579 M2 — vector-substrate requires shapes at CLI preflight: rejects
  // unknown engines, malformed `vector:` values, and double-declares.
  // parseRequires re-runs the same analysis on the generator/deployer path.
  validateVectorRequires(config.requires);
  if (dbShapes.external.length > 0) {
    const vaultEnvNames = new Set(
      isPlainObject(rawVault) ? Object.keys(rawVault) : [],
    );
    for (const ext of dbShapes.external) {
      if (!vaultEnvNames.has(ext.secret_ref)) {
        const yamlKey = ext.kind === 'mongodb' ? 'db' : 'redis';
        die(
          `\`{${yamlKey}: external}\` requires \`secret: <NAME>\` to reference ` +
          `a key in your \`vault:\` block; \`${ext.secret_ref}\` not found in vault.`,
        );
      }
    }
  }

  // Backlog 229 — Connectors v1: validate the three new top-level blocks.
  // Real connector lookup + scope-permission checks happen server-side at
  // plug time; here we only enforce shape so a malformed yaml fails fast.
  validateConnectorsBlock((config as { connectors?: unknown }).connectors);
  const connectorIds = collectConnectorIds(
    (config as { connectors?: unknown }).connectors,
  );
  validateHooksBlock((config as { hooks?: unknown }).hooks, connectorIds);
  validateTriggersBlock((config as { triggers?: unknown }).triggers, connectorIds);
}

// Backlog 229 — Connectors v1 shape checks. Kept inline below validateConfig
// because the strict-keys helpers (`checkUnknownKeys`, `isPlainObject`,
// `die`) live in this module.

function validateConnectorsBlock(raw: unknown): void {
  if (raw === undefined) return;
  if (!Array.isArray(raw)) {
    die(`'connectors:' must be a list (got ${typeOf(raw)})`);
  }
  raw.forEach((entry, idx) => {
    if (!isPlainObject(entry)) {
      die(`connectors[${idx}] must be an object (got ${typeOf(entry)})`);
    }
    checkUnknownKeys(entry, CONNECTORS_ENTRY_KEYS, `connectors[${idx}]`);
    const id = entry.id;
    if (typeof id !== 'string' || !CONNECTOR_ID_PATTERN.test(id)) {
      die(
        `connectors[${idx}].id must be 3–40 chars, lowercase letters/digits/hyphens, ` +
        `starting with a letter (got ${JSON.stringify(id)})`,
      );
    }
    const target = entry.target;
    if (typeof target !== 'string' || !VALID_CONNECTOR_TARGETS.has(target)) {
      die(
        `connectors[${idx}].target must be one of: ${Array.from(VALID_CONNECTOR_TARGETS).join(' | ')} ` +
        `(got ${JSON.stringify(target)})`,
      );
    }
    if (entry.channel !== undefined && typeof entry.channel !== 'string') {
      die(`connectors[${idx}].channel must be a string when set`);
    }
  });
}

function collectConnectorIds(raw: unknown): Set<string> {
  const ids = new Set<string>();
  if (!Array.isArray(raw)) return ids;
  for (const entry of raw) {
    if (isPlainObject(entry) && typeof entry.id === 'string') ids.add(entry.id);
  }
  return ids;
}

function validateHooksBlock(raw: unknown, connectorIds: Set<string>): void {
  if (raw === undefined) return;
  if (!Array.isArray(raw)) {
    die(`'hooks:' must be a list (got ${typeOf(raw)})`);
  }
  raw.forEach((entry, idx) => {
    if (!isPlainObject(entry)) {
      die(`hooks[${idx}] must be an object (got ${typeOf(entry)})`);
    }
    checkUnknownKeys(entry, HOOKS_ENTRY_KEYS, `hooks[${idx}]`);
    const on = entry.on;
    if (typeof on !== 'string' || !VALID_HOOK_TRIGGERS.has(on)) {
      die(
        `hooks[${idx}].on must be one of: ${Array.from(VALID_HOOK_TRIGGERS).join(' | ')} ` +
        `(got ${JSON.stringify(on)})`,
      );
    }
    const via = entry.via;
    if (typeof via !== 'string' || via.length === 0) {
      die(`hooks[${idx}].via must be a non-empty connector id`);
    }
    if (!connectorIds.has(via)) {
      die(
        `hooks[${idx}].via '${via}' does not match any connector id in this yaml's 'connectors:' block. ` +
        `Declare the connector first, then reference it from hooks.`,
      );
    }
    if (typeof entry.template !== 'string' || entry.template.length === 0) {
      die(`hooks[${idx}].template must be a non-empty string`);
    }
    if (entry.channel !== undefined && typeof entry.channel !== 'string') {
      die(`hooks[${idx}].channel must be a string when set`);
    }
  });
}

function validateTriggersBlock(raw: unknown, connectorIds: Set<string>): void {
  if (raw === undefined) return;
  if (!Array.isArray(raw)) {
    die(`'triggers:' must be a list (got ${typeOf(raw)})`);
  }
  raw.forEach((entry, idx) => {
    if (!isPlainObject(entry)) {
      die(`triggers[${idx}] must be an object (got ${typeOf(entry)})`);
    }
    checkUnknownKeys(entry, TRIGGERS_ENTRY_KEYS, `triggers[${idx}]`);
    const from = entry.from;
    if (typeof from !== 'string' || from.length === 0) {
      die(`triggers[${idx}].from must be a non-empty connector id`);
    }
    if (!connectorIds.has(from)) {
      die(
        `triggers[${idx}].from '${from}' does not match any connector id in this yaml's 'connectors:' block. ` +
        `Declare the connector first, then reference it from triggers.`,
      );
    }
    const event = entry.event;
    // v1 inbound is `message`-from-channel only. Accept any non-empty
    // string so future providers (webhook event ids) don't need a
    // validator change, but reject the empty-string / non-string cases.
    if (typeof event !== 'string' || event.length === 0) {
      die(`triggers[${idx}].event must be a non-empty string (v1 supports 'message' for Slack)`);
    }
    if (entry.channel !== undefined && typeof entry.channel !== 'string') {
      die(`triggers[${idx}].channel must be a string when set`);
    }
    const route = entry.route;
    if (typeof route !== 'string' || !route.startsWith('/')) {
      die(`triggers[${idx}].route must be a string starting with '/' (got ${JSON.stringify(route)})`);
    }
  });
}

function typeOf(value: unknown): string {
  if (Array.isArray(value)) return 'list';
  if (value === null) return 'null';
  return typeof value;
}

// Decision 023 Update 2026-05-18 — vault map validation patterns.
//
// `VAULT_ENV_NAME_PATTERN` matches the standard POSIX-style env-var-name
// shape (first char a letter; rest letters/digits/underscores). This is
// stricter than the vault item key pattern, which accepts a leading digit,
// because env vars with a leading digit are invalid in most shells.
//
// `VAULT_ITEM_KEY_PATTERN` mirrors `KEY_PATTERN` in routes/v2/vault.ts so a
// yaml entry whose value passes here is guaranteed to be a syntactically
// legal vault item key (existence check happens at plug time).
const VAULT_ENV_NAME_PATTERN = /^[A-Z][A-Z0-9_]*$/;
const VAULT_ENV_NAME_MAX_LEN = 64;
const VAULT_ITEM_KEY_PATTERN = /^[A-Z0-9_]+$/;
const VAULT_ITEM_KEY_MAX_LEN = 64;

/**
 * Backlog 201 — collect names of resources declared as `<name>: private` in a
 * yaml's `requires:` list. Returns an empty set when `requires` is absent,
 * malformed, or contains only shared entries. This lives next to parseRequires
 * but is intentionally lenient — it runs during validateConfig's cross-ref
 * pass where the broader requires shape may not yet have been validated; bad
 * shapes will surface with their own dedicated message via parseRequires
 * downstream.
 */
export function collectPrivateRequiresNames(
  requires: CloudGridYaml['requires'],
): Set<string> {
  const out = new Set<string>();
  if (!Array.isArray(requires)) return out;
  for (const entry of requires) {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) continue;
    const keys = Object.keys(entry);
    if (keys.length !== 1) continue;
    const key = keys[0];
    const val = (entry as Record<string, unknown>)[key];
    if (val !== 'private') continue;
    // DB W1.C — `{db: private}` is deliberately NOT collected yet: the
    // entity-namespace Mongo renderer lands in DB Phase 4, so there is no
    // Deployment/Service an initContainer could probe.
    if (key === 'db') continue;
    out.add(key);
  }
  return out;
}

/**
 * Backlog 277 — error text for a `requires:` entry that names a shared
 * service not in the registry (e.g. Asaf's `ai`). The old message stated
 * only what was wrong; this one adds a close-match suggestion when the typo
 * is near a real service and always lists what IS available, so the user can
 * recover without opening shared-services.yaml. §23 voice — blameless,
 * specific, actionable.
 */
function formatUnknownSharedServiceError(
  entry: string,
  registry: SharedServicesRegistry,
): string {
  const available = Object.keys(registry).sort();
  const lines: string[] = [];
  lines.push(`Unknown shared service: '${entry}' (not in shared-services.yaml).`);
  lines.push('');
  const suggestion = didYouMeanLine(entry, available);
  if (suggestion) {
    lines.push(`  ${suggestion}`);
    lines.push('');
  }
  lines.push(
    available.length > 0
      ? `  Available shared services: ${available.join(', ')}.`
      : '  No shared services are registered.',
  );
  return lines.join('\n');
}

/**
 * DB W1.C (spec 2026-06-06 §2) — normalized view of the DB-tier shapes in a
 * `requires:` list. Produced by {@link validateDbRequires}; merged into
 * {@link ParsedRequires} by parseRequires and cross-checked against the
 * `vault:` block by validateConfig.
 */
export interface DbRequiresShapes {
  /** String entry `db` or `mongodb` present (org-tier shared Mongo). */
  sharedMongo: boolean;
  /** String entry `redis` present (org-tier shared Redis). */
  sharedRedis: boolean;
  /** `{db: private}` — entity-namespace Mongo. */
  privateMongo: boolean;
  /** `{redis: private}`. */
  privateRedis: boolean;
  external: ExternalDbRequirement[];
  egress: NativeEgressRequirement[];
}

/** Yaml form used in W1.C validation messages, per kind and mode. */
const DB_MODE_LABELS: Record<'mongodb' | 'redis', Record<'shared' | 'private' | 'external', string>> = {
  mongodb: {
    shared: 'shared `db`',
    private: '`{db: private}`',
    external: '`{db: external}`',
  },
  redis: {
    shared: 'shared `redis`',
    private: '`{redis: private}`',
    external: '`{redis: external}`',
  },
};

/**
 * Whether a requires entry is one of the DB W1.C object shapes that
 * {@link validateDbRequires} owns: `{db|redis: private}`,
 * `{db|redis: external, secret}`, or `{external_db: native, port}`.
 * parseRequires skips these in its own loop — the analyzer already
 * validated and normalized them.
 */
function isDbShapeRequiresEntry(entry: Record<string, unknown>): boolean {
  if ('external_db' in entry) return true;
  return entry['db'] !== undefined || entry['redis'] !== undefined;
}

/**
 * DB W1.C (spec 2026-06-06 §2.1–2.3) — validate and normalize the DB-tier
 * `requires:` shapes. Dies on:
 *   - the historical `{mongodb: private}` form (MONGODB_PRIVATE_DEPRECATED)
 *   - per-kind mode conflicts (shared vs private vs external — pick one)
 *   - `{db|redis: external}` without a `secret:` name
 *   - native egress ports outside {@link EXTERNAL_NATIVE_DB_PORTS} or
 *     declared twice
 *
 * Runs in BOTH validation layers: validateConfig (CLI preflight + server
 * yaml validation — where the `vault:` cross-check also lives) and
 * parseRequires (generator/deployer path). Entries that are not DB-shaped
 * (other strings, `gcp:`, unknown modifiers) are ignored here — they keep
 * their existing dedicated errors downstream.
 */
export function validateDbRequires(
  requires: CloudGridYaml['requires'],
): DbRequiresShapes {
  const shapes: DbRequiresShapes = {
    sharedMongo: false,
    sharedRedis: false,
    privateMongo: false,
    privateRedis: false,
    external: [],
    egress: [],
  };
  if (!Array.isArray(requires)) return shapes;

  const seenEgressPorts = new Set<number>();

  for (const entry of requires) {
    if (typeof entry === 'string') {
      const name = REQUIRES_KIND_ALIASES[entry] ?? entry;
      if (name === 'mongodb') shapes.sharedMongo = true;
      if (name === 'redis') shapes.sharedRedis = true;
      continue;
    }
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) continue;
    const obj = entry as Record<string, unknown>;

    // §2.3 — the historical form is rejected, never converted.
    if (obj['mongodb'] === 'private') {
      die(MONGODB_PRIVATE_DEPRECATED_MESSAGE);
    }

    // `{external_db: native, port: N}` — egress declaration (§1.2 #9).
    if ('external_db' in obj) {
      if (obj['external_db'] !== 'native') {
        die(
          `Unsupported external_db mode: ${JSON.stringify(obj['external_db'])}. ` +
          "Only `{external_db: native, port: <n>}` is supported.",
        );
      }
      const port = obj['port'];
      if (typeof port !== 'number' || !Number.isInteger(port)) {
        die(
          '`{external_db: native}` requires an integer `port:` ' +
          `(got ${JSON.stringify(port)}).`,
        );
      }
      if (!EXTERNAL_NATIVE_DB_PORTS.includes(port)) {
        die(
          `External native ports must be a recognized DB port ` +
          `(${EXTERNAL_NATIVE_DB_PORTS.join(', ')}); got ${port}. ` +
          'To open arbitrary ports, contact platform admin.',
        );
      }
      if (seenEgressPorts.has(port)) {
        die(
          `Duplicate \`{external_db: native, port: ${port}}\` declaration. ` +
          'Declare each port once.',
        );
      }
      seenEgressPorts.add(port);
      shapes.egress.push({ kind: 'egress', port });
      continue;
    }

    // `{db|redis: <mode>}` — private and external for both kinds.
    for (const yamlKey of ['db', 'redis'] as const) {
      const mode = obj[yamlKey];
      if (mode === undefined) continue;
      const kind: 'mongodb' | 'redis' = yamlKey === 'db' ? 'mongodb' : 'redis';
      if (mode === 'private') {
        if (yamlKey === 'db') shapes.privateMongo = true;
        else shapes.privateRedis = true;
      } else if (mode === 'external') {
        const secret = obj['secret'];
        if (typeof secret !== 'string' || secret.length === 0) {
          die(
            `\`{${yamlKey}: external}\` requires \`secret: <NAME>\` referencing ` +
            'a key in your `vault:` block.',
          );
        }
        shapes.external.push({ kind, mode: 'external', secret_ref: secret });
      } else {
        die(
          `Unsupported requires mode: ${yamlKey}: ${JSON.stringify(mode)} ` +
          "(supported modes: 'private', 'external').",
        );
      }
    }
  }

  // §2.2 — per-kind mutual exclusion: shared vs private vs external.
  for (const kind of ['mongodb', 'redis'] as const) {
    const labels = DB_MODE_LABELS[kind];
    const externalCount = shapes.external.filter((e) => e.kind === kind).length;
    const modes: Array<'shared' | 'private' | 'external'> = [];
    if (kind === 'mongodb' ? shapes.sharedMongo : shapes.sharedRedis) modes.push('shared');
    if (kind === 'mongodb' ? shapes.privateMongo : shapes.privateRedis) modes.push('private');
    if (externalCount > 0) modes.push('external');
    if (modes.length > 1) {
      die(
        `Cannot use both ${labels[modes[0]]} and ${labels[modes[1]]} ` +
        'for the same entity. Pick one.',
      );
    }
    if (externalCount > 1) {
      die(
        `Multiple ${labels.external} entries for the same entity. Declare one.`,
      );
    }
  }

  return shapes;
}

/**
 * BL579 M2 — normalized view of a yaml's vector-substrate `requires:`
 * declaration. `engine` is the explicit pin from yaml; `undefined` means the
 * entity wrote the bare `vector` (or `{ vector: {} }`) form and the deploy-time
 * resolver should fall back to `platform_settings.default_vector_engine`.
 */
export interface ParsedVectorRequires {
  /** Explicit engine pin, or `undefined` → resolve the platform default at deploy time. */
  engine?: VectorEngine;
  /**
   * BL579 M2-wiring — optional embedding dimensionality hint
   * (`{ vector: { engine, dim: 1536 } }`). Persisted on the deploy-event
   * checkpoint for forward-compat; it is NOT consumed by the v1 provisioner
   * (the schema/role are created dimension-agnostic — a column's dimension is
   * the entity's own DDL concern). A later M-x can read it to pre-create a
   * typed `vector(dim)` column. Must be a positive integer when present.
   */
  dim?: number;
}

/**
 * BL579 M2 (backlog 579 §3) — validate + normalize the vector-substrate shapes
 * in a `requires:` list. Returns `null` when the entity declares no vector
 * substrate; the normalized `{ engine? }` view otherwise. Dies on:
 *   - an unknown `engine:` value (not in {@link SUPPORTED_VECTOR_ENGINES})
 *   - a malformed `vector:` value (not a string or `{ engine }` object)
 *   - declaring the vector substrate more than once
 *
 * Recognizes three accepted forms (all equivalent for `pgvector`):
 *   - `vector`                       (bare string → default engine)
 *   - `{ vector: pgvector }`         (flat-string engine pin)
 *   - `{ vector: { engine: pgvector } }` (object engine pin; extensible)
 *
 * Runs in BOTH validation layers — validateConfig (CLI preflight + server yaml
 * validation) and parseRequires (generator/deployer path) — so a bad engine is
 * rejected identically wherever the yaml is read. Non-vector entries are
 * ignored; they keep their existing dedicated errors elsewhere.
 */
export function validateVectorRequires(
  requires: CloudGridYaml['requires'],
): ParsedVectorRequires | null {
  if (!Array.isArray(requires)) return null;

  let declared = false;
  let occurrences = 0;
  let engine: VectorEngine | undefined;
  let dim: number | undefined;

  const assertDim = (raw: unknown): number => {
    if (typeof raw !== 'number' || !Number.isInteger(raw) || raw <= 0) {
      die(
        `Invalid vector \`dim\`: ${JSON.stringify(raw)}. ` +
          `Expected a positive integer (the embedding dimensionality, e.g. 1536).`,
      );
    }
    return raw as number;
  };

  const assertEngine = (raw: unknown): VectorEngine => {
    if (
      typeof raw !== 'string' ||
      !(SUPPORTED_VECTOR_ENGINES as readonly string[]).includes(raw)
    ) {
      const hint =
        typeof raw === 'string'
          ? didYouMeanLine(raw, [...SUPPORTED_VECTOR_ENGINES])
          : undefined;
      die(
        `Unknown vector engine: ${JSON.stringify(raw)}. ` +
          `Supported engines: ${SUPPORTED_VECTOR_ENGINES.join(', ')}.` +
          (hint ? ` ${hint}` : ''),
      );
    }
    return raw as VectorEngine;
  };

  for (const entry of requires) {
    // Bare-string form: `requires: [vector]` → platform default engine.
    if (entry === VECTOR_REQUIRES_KEYWORD) {
      declared = true;
      occurrences++;
      continue;
    }
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) continue;
    const obj = entry as Record<string, unknown>;
    if (!(VECTOR_REQUIRES_KEYWORD in obj)) continue;

    declared = true;
    occurrences++;
    const value = obj[VECTOR_REQUIRES_KEYWORD];

    if (typeof value === 'string') {
      // `{ vector: pgvector }` — flat-string engine pin.
      engine = assertEngine(value);
    } else if (isPlainObject(value)) {
      // `{ vector: { engine: pgvector } }` — object engine pin. `{ vector: {} }`
      // is the explicit "use the platform default" form (engine stays undefined).
      checkUnknownKeys(value, REQUIRES_VECTOR_OPTION_KEYS, 'requires[].vector');
      const rawEngine = (value as Record<string, unknown>)['engine'];
      if (rawEngine !== undefined) engine = assertEngine(rawEngine);
      const rawDim = (value as Record<string, unknown>)['dim'];
      if (rawDim !== undefined) dim = assertDim(rawDim);
    } else {
      die(
        'Invalid `vector` requirement: expected `vector` (default engine), ' +
          '`{vector: pgvector}`, or `{vector: {engine: pgvector}}`, got ' +
          `${JSON.stringify(value)}.`,
      );
    }
  }

  if (occurrences > 1) {
    die(
      'Duplicate `vector` requirement: declare the vector substrate at most ' +
        'once in `requires:`.',
    );
  }

  return declared ? { engine, dim } : null;
}

export function parseRequires(
  requires: CloudGridYaml['requires'],
  registry: SharedServicesRegistry,
): ParsedRequires {
  const result: ParsedRequires = { shared: [], privateRedis: false };
  if (!requires) return result;

  // DB W1.C — validate + normalize the DB-tier shapes first (rejects
  // {mongodb: private}, mode conflicts, bad native ports) and merge the
  // normalized view. The vault cross-check for `{db|redis: external}`
  // lives in validateConfig, which sees the whole yaml.
  const dbShapes = validateDbRequires(requires);
  if (dbShapes.privateMongo) result.privateMongo = true;
  if (dbShapes.privateRedis) result.privateRedis = true;
  if (dbShapes.external.length > 0) result.external = dbShapes.external;
  if (dbShapes.egress.length > 0) result.egress = dbShapes.egress;

  // BL579 M2 — validate + normalize the vector-substrate declaration (rejects
  // unknown engines, malformed shapes, double-declares). `vector` is a
  // platform-managed keyword, NOT a shared-services.yaml entry, so it bypasses
  // the registry check below.
  const vectorReq = validateVectorRequires(requires);
  if (vectorReq) result.vector = vectorReq;

  for (const entry of requires) {
    if (typeof entry === 'string') {
      // BL579 M2 — the bare `vector` keyword is a platform substrate, not a
      // shared service; it was normalized into result.vector above. Skip the
      // registry check (it isn't in shared-services.yaml).
      if (entry === VECTOR_REQUIRES_KEYWORD) continue;
      // DB W1.C — `db` is a yaml-level alias; the registry keeps the
      // canonical name. Dedupe so `[db, mongodb]` yields one entry.
      const name = REQUIRES_KIND_ALIASES[entry] ?? entry;
      if (!registry[name]) {
        die(formatUnknownSharedServiceError(entry, registry));
      }
      if (!result.shared.includes(name)) result.shared.push(name);
    } else if (typeof entry === 'object' && entry !== null) {
      const obj = entry as Record<string, unknown>;
      // DB-tier object shapes were validated + normalized above.
      if (isDbShapeRequiresEntry(obj)) continue;
      // BL579 M2 — `{ vector: ... }` was validated + normalized above.
      if (VECTOR_REQUIRES_KEYWORD in obj) continue;
      const key = Object.keys(entry)[0];
      if (key === 'gcp' && isGcpRequires(entry)) {
        const gcpEntry = entry as GcpRequires;
        if (!Array.isArray(gcpEntry.gcp.services) || gcpEntry.gcp.services.length === 0) {
          die('gcp.services must be a non-empty array');
        }
        for (const svc of gcpEntry.gcp.services) {
          if (!VALID_GCP_SERVICES.includes(svc as any)) {
            const hint = didYouMeanLine(String(svc), VALID_GCP_SERVICES);
            die(
              `Invalid GCP service: '${svc}'. Must be one of: ${VALID_GCP_SERVICES.join(', ')}.` +
              (hint ? ` ${hint}` : ''),
            );
          }
        }
        result.gcp = { services: [...gcpEntry.gcp.services] };
      } else {
        const val = (entry as Record<string, string>)[key];
        die(
          `Unsupported requires modifier: ${key}: ${val} (supported: ` +
          "'db: private', 'redis: private', " +
          "'db: external', 'redis: external', 'external_db: native', " +
          "and 'gcp: { services: [...] }')",
        );
      }
    } else {
      die(`Invalid requires entry: ${JSON.stringify(entry)}`);
    }
  }

  return result;
}

/**
 * Backlog 082 — exact remediation message for the no-routable-surface
 * failure. The wording is the spec; do not paraphrase. Carried in a helper
 * so the message stays in one place for both the validate-side check and
 * the CLI preflight (which reuses validateConfig with kind='app').
 */
function formatRoutabilityError(entityName: string): string {
  return [
    `Validation failed: app '${entityName}' has no routable surface.`,
    '',
    '  No service in cloudgrid.yaml declares a `path:`. An app without a path',
    '  cannot serve HTTP traffic.',
    '',
    '  Pick one:',
    '    1. Add `path: /` to the service that should be the public entry point',
    '       (typically the frontend or dashboard).',
    '    2. Add `path:` to multiple services with different prefixes',
    '       (e.g. dashboard `/`, content-pipeline `/api`).',
    '    3. If this entity is internal-only (cron-only, background worker),',
    '       declare it explicitly:',
    '           expose: false',
    '',
    '  Example:',
    '    services:',
    '      dashboard:',
    '        type: nextjs',
    '        path: /                  # ← add this',
    '      content-pipeline:',
    '        type: node',
    '        path: /api               # ← add this',
  ].join('\n');
}

/**
 * Backlog 092 — soft-warning text emitted when a non-cron service in a
 * kind=app entity omits `path:`. The yaml still validates; the suggestion
 * is the explicit `path: false` form so the renderer report (backlog 087)
 * can name the developer's choice instead of inferring it from absence.
 *
 * Wording follows §23 voice — plain, specific, no exclamation marks.
 */
function formatPathAbsentWarning(serviceName: string): string {
  return [
    `Notice: service '${serviceName}' in cloudgrid.yaml has no 'path:' declared.`,
    '',
    `  This service will be deployed as ClusterIP-only (internal-only). If`,
    `  that's intentional, add 'path: false' to the service block to make the`,
    `  decision explicit. If you intended this service to be reachable from`,
    `  the internet, add path: '/' (or another path prefix).`,
    '',
    `  Example:`,
    `    services:`,
    `      ${serviceName}:`,
    `        type: node`,
    `        path: false              # explicit: internal-only`,
    '',
    `  This warning is informational; the deploy continues unchanged.`,
  ].join('\n');
}

/**
 * Backlog 101 — error text for an invalid `health:` value. The field is
 * optional (TCP probe is the 098 default); when set it must be `true`,
 * `false`, or a path starting with `/`. Wording follows §23 voice — plain,
 * specific, no exclamation marks.
 */
function formatHealthInvalidError(serviceName: string, value: unknown): string {
  return [
    `Service '${serviceName}' has an invalid 'health:' value: ${JSON.stringify(value)}.`,
    '',
    `  The 'health:' field is optional. When set, it must be one of:`,
    `    - a path starting with '/'  (HTTP probe at that path, e.g. '/api/health')`,
    `    - true                       (HTTP probe at '/health')`,
    `    - false                      (TCP probe — same as omitting the field)`,
    '',
    `  Example:`,
    `    services:`,
    `      ${serviceName}:`,
    `        type: node`,
    `        path: /`,
    `        health: /api/health      # HTTP probe at /api/health`,
  ].join('\n');
}

/**
 * Backlog 104 — error text for a malformed custom_domains entry. The field
 * accepts only fully-qualified domain names (lowercase, ≥2 labels, no
 * wildcards, no IP addresses, no whitespace). Wording follows §23 voice —
 * plain, specific, no exclamation marks.
 */
function formatCustomDomainInvalidError(value: string): string {
  return [
    `Invalid custom_domains entry: ${JSON.stringify(value)}.`,
    '',
    `  Each entry must be a fully-qualified domain name. Wildcards, IP`,
    `  addresses, and bare hostnames are not accepted.`,
    '',
    `  Example:`,
    `    custom_domains:`,
    `      - console.cloudgrid.io`,
  ].join('\n');
}

/**
 * Backlog 104 — error text for an out-of-scope custom_domains entry. The
 * MVP supports cloudgrid.io and its subdomains only; tenant-owned domains
 * land in backlog 016. Wording follows §23 voice.
 */
function formatCustomDomainOutOfScopeError(domain: string): string {
  return [
    `Custom domain '${domain}' is outside the supported scope for this release.`,
    '',
    `  The MVP supports custom domains under cloudgrid.io (subdomains and`,
    `  the root apex). Tenant-owned domains land in backlog 016.`,
    '',
    `  Example:`,
    `    custom_domains:`,
    `      - console.cloudgrid.io`,
  ].join('\n');
}

/**
 * Backlog 104 — deprecation warning emitted when the legacy 'domain:' field
 * is auto-translated into custom_domains[]. Wording follows §23 voice.
 */
function formatDomainDeprecatedWarning(domain: string): string {
  return [
    `Notice: 'domain: ${domain}' is deprecated. Use 'custom_domains:' instead.`,
    '',
    `  cloudgrid.yaml:`,
    `    custom_domains:`,
    `      - ${domain}`,
    '',
    `  Auto-translated for this deploy. The 'domain:' field will be removed`,
    `  from the schema after the next release window.`,
  ].join('\n');
}

/**
 * Backlog 191 — deprecation warning emitted when the legacy `network.public`
 * field is auto-translated onto the new `expose` field. Wording follows §23
 * voice — plain, specific, actionable.
 */
function formatNetworkPublicDeprecatedWarning(): string {
  return [
    `Notice: 'network.public' is deprecated. Use 'expose' instead.`,
    '',
    `  cloudgrid.yaml:`,
    `    expose: false           # or true; replaces network.public`,
    '',
    `  Auto-translated for this deploy. The 'network.public' field will be`,
    `  removed from the schema in 0.8.15.`,
  ].join('\n');
}

/**
 * Backlog 191 — error emitted when a yaml carries both `expose:` and
 * `network.public:` with disagreeing values. Silently picking one would
 * mask the developer's intent.
 */
function formatExposeConflictError(exposeVal: boolean, legacyVal: boolean): string {
  return [
    `Both 'expose' and 'network.public' are set and disagree:`,
    `  expose: ${exposeVal}`,
    `  network.public: ${legacyVal}`,
    '',
    `  'network.public' is the legacy name for 'expose' and the values must`,
    `  match. Remove 'network.public' — it is deprecated and will be removed`,
    `  in 0.8.15.`,
  ].join('\n');
}

/**
 * Backlog 104 / BL657 — informational warning when a legacy *.apps.cloudgrid.io
 * domain is dropped during migration. The legacy apps wildcard has been retired
 * (decision 021); the entity now serves on its default URL only. Wording follows
 * §23 voice.
 */
function formatLegacyAppsDomainWarning(domain: string): string {
  return [
    `Notice: 'domain: ${domain}' targets the retired *.apps.cloudgrid.io wildcard.`,
    '',
    `  This wildcard no longer serves. The entity now serves only on its`,
    `  default URL. To declare a custom URL, use 'custom_domains:'.`,
    '',
    `  This warning is informational; the deploy continues unchanged.`,
  ].join('\n');
}

/**
 * Backlog 108 — accept relative directory names for `services.<svc>.build.output`.
 * Rejects absolute paths, parent traversal, and shell metacharacters so the
 * server can pass the value safely into the Cloud Build step and the
 * synthesized Dockerfile's `COPY` instruction.
 */
function isValidBuildOutput(value: string): boolean {
  if (value.length === 0 || value.length > 100) return false;
  if (value.startsWith('/')) return false;
  // Reject any `..` segment; bare `.` is also rejected (would alias the
  // service root and confuse the validator's build-vs-no-build branch).
  const segments = value.split('/');
  for (const seg of segments) {
    if (seg === '' || seg === '.' || seg === '..') return false;
    if (!/^[a-zA-Z0-9._-]+$/.test(seg)) return false;
  }
  return true;
}

function formatBuildTypeError(serviceName: string, type: string): string {
  return [
    `Service '${serviceName}' has a 'build:' block but type is '${type}'.`,
    '',
    `  The 'build:' block is only valid for type: static services. It runs`,
    `  a build command server-side and bundles the produced output (default`,
    `  dist/) into the static layer.`,
    '',
    `  Pick one:`,
    `    1. Remove the 'build:' block.`,
    `    2. Change type to 'static' if this service produces static HTML.`,
  ].join('\n');
}

function formatBuildShapeError(serviceName: string): string {
  return [
    `Service '${serviceName}' has an invalid 'build:' value.`,
    '',
    `  The 'build:' block must be an object with at minimum a 'command'`,
    `  string. 'output' is optional and defaults to 'dist'.`,
    '',
    `  Example:`,
    `    services:`,
    `      ${serviceName}:`,
    `        type: static`,
    `        build:`,
    `          command: npm run build`,
    `          output: dist`,
  ].join('\n');
}

function formatBuildCommandError(serviceName: string): string {
  return [
    `Service '${serviceName}' has a 'build:' block but no 'command' string.`,
    '',
    `  The 'command' field is required and must be a non-empty string. It`,
    `  runs server-side inside the service folder before the static layer`,
    `  is bundled.`,
    '',
    `  Example:`,
    `    services:`,
    `      ${serviceName}:`,
    `        type: static`,
    `        build:`,
    `          command: npm run build`,
  ].join('\n');
}

function formatBuildOutputError(serviceName: string, value: unknown): string {
  return [
    `Service '${serviceName}' has an invalid 'build.output' value: ${JSON.stringify(value)}.`,
    '',
    `  The 'output' field is optional (defaults to 'dist'). When set, it`,
    `  must be a relative directory name — no leading '/', no '..' segments,`,
    `  no shell metacharacters.`,
    '',
    `  Example:`,
    `    services:`,
    `      ${serviceName}:`,
    `        type: static`,
    `        build:`,
    `          command: npm run build`,
    `          output: dist`,
  ].join('\n');
}

function formatBuildEnvShapeError(serviceName: string): string {
  return [
    `Service '${serviceName}' has an invalid 'build.env' value.`,
    '',
    `  The 'build.env' field is optional. When set, it must be a map of`,
    `  string-keyed env-var names to string values. The values are exported`,
    `  into the static-build step before 'command' runs so build tools like`,
    `  Vite (VITE_*) or Next.js (NEXT_PUBLIC_*) can bake them into the bundle.`,
    '',
    `  Different from 'env:' on the service — runtime env lands in the pod`,
    `  ConfigMap, while 'build.env' only exists while the build runs.`,
    '',
    `  Example:`,
    `    services:`,
    `      ${serviceName}:`,
    `        type: static`,
    `        build:`,
    `          command: npm run build`,
    `          env:`,
    `            VITE_API_URL: https://api.example.com`,
  ].join('\n');
}

function formatBuildEnvNameError(serviceName: string, envKey: string): string {
  return [
    `Service '${serviceName}' has an invalid 'build.env' key: ${JSON.stringify(envKey)}.`,
    '',
    `  Build env keys must be POSIX-style env var names — start with an`,
    `  uppercase letter or underscore, then uppercase letters, digits, and`,
    `  underscores only.`,
    '',
    `  Example:`,
    `    build:`,
    `      command: npm run build`,
    `      env:`,
    `        VITE_API_URL: https://api.example.com`,
  ].join('\n');
}

function formatBuildEnvValueError(serviceName: string, envKey: string, value: unknown): string {
  return [
    `Service '${serviceName}' has a non-string value for 'build.env.${envKey}': ${JSON.stringify(value)}.`,
    '',
    `  Every 'build.env' value must be a string. yaml numbers, booleans, lists,`,
    `  and maps are rejected — quote the value so yaml keeps it a string.`,
    '',
    `  Example:`,
    `    build:`,
    `      command: npm run build`,
    `      env:`,
    `        VITE_FEATURE_FLAG: 'true'   # quoted; not bare true`,
  ].join('\n');
}

function formatBuildEnvReservedError(serviceName: string, envKey: string): string {
  return [
    `Service '${serviceName}' tries to set the reserved build env key '${envKey}'.`,
    '',
    `  These keys are owned by the platform and cannot be set via`,
    `  'build.env':`,
    '',
    `    - PORT, APP_NAME, SERVICE_NAME, NODE_ENV`,
    `    - any key starting with 'CLOUDGRID_'`,
    '',
    `  Pick a different name. For build-time API URLs that must be baked`,
    `  into the bundle, prefix with the framework's public-var convention`,
    `  (e.g. VITE_API_URL, NEXT_PUBLIC_API_URL).`,
  ].join('\n');
}

function formatNodeVersionTypeError(serviceName: string, type: string): string {
  return [
    `Service '${serviceName}' has a 'node_version' field but type is '${type}'.`,
    '',
    `  The 'node_version' field selects the node image the static-build step`,
    `  runs inside. Only type: static services use it — every other type`,
    `  builds from a language-specific template that already pins its runtime.`,
    '',
    `  Pick one:`,
    `    1. Remove the 'node_version' field.`,
    `    2. Change type to 'static' if this service produces static HTML`,
    `       and you need a non-default Node version for the build.`,
  ].join('\n');
}

function formatNodeVersionValueError(serviceName: string, value: unknown): string {
  return [
    `Service '${serviceName}' has an invalid 'node_version' value: ${JSON.stringify(value)}.`,
    '',
    `  Supported values are ${VALID_NODE_VERSIONS.map((v) => `'${v}'`).join(', ')}.`,
    `  Quote the value so yaml keeps it a string — bare ${VALID_NODE_VERSIONS[1]} parses`,
    `  as an integer and is rejected.`,
    '',
    `  Example:`,
    `    services:`,
    `      ${serviceName}:`,
    `        type: static`,
    `        node_version: '20'`,
    `        build:`,
    `          command: npm run build`,
  ].join('\n');
}

function formatBaseImageTypeError(serviceName: string, type: string): string {
  return [
    `Service '${serviceName}' has a 'base_image' field but type is '${type}'.`,
    '',
    `  The 'base_image' field selects the final-stage base image for the`,
    `  Next.js template (distroless by default, 'alpine' to opt out). Only`,
    `  type: nextjs services use it — every other type builds from a template`,
    `  that already pins its own runtime base image.`,
    '',
    `  Remove the 'base_image' field, or change type to 'nextjs'.`,
  ].join('\n');
}

function formatBaseImageValueError(serviceName: string, value: unknown): string {
  return [
    `Service '${serviceName}' has an invalid 'base_image' value: ${JSON.stringify(value)}.`,
    '',
    `  Supported values are 'distroless' (the default) and 'alpine'.`,
    '',
    `  Example:`,
    `    services:`,
    `      ${serviceName}:`,
    `        type: nextjs`,
    `        base_image: alpine`,
  ].join('\n');
}

function formatPortTypeError(serviceName: string): string {
  return [
    `Service '${serviceName}' has a 'port' field but type is 'cron'.`,
    '',
    `  Cron services don't listen on a port — the renderer emits a CronJob,`,
    `  not a Deployment + Service. Remove the 'port' field, or change the`,
    `  service type if it should expose an HTTP surface.`,
  ].join('\n');
}

function formatPortValueError(serviceName: string, value: unknown): string {
  return [
    `Service '${serviceName}' has an invalid 'port' value: ${JSON.stringify(value)}.`,
    '',
    `  The 'port' field is optional. When set, it must be an integer in`,
    `  the TCP port range (1-65535) — the port the container binds on`,
    `  inside the pod. Omit the field to use the platform default (8080).`,
    '',
    `  Example:`,
    `    services:`,
    `      ${serviceName}:`,
    `        type: node`,
    `        port: 3000`,
  ].join('\n');
}

/**
 * Backlog 124 — accept relative directory paths for `services.<svc>.source.path`.
 * Rejects absolute paths, `..` traversal, empty strings, shell metacharacters,
 * whitespace, and null bytes so the value is safe to pass to a `tar -C`
 * invocation client-side and to the Cloud Build storage source server-side.
 */
function isValidSourcePath(value: string): boolean {
  if (value.length === 0 || value.length > 200) return false;
  if (value.startsWith('/')) return false;
  if (value.includes('\\')) return false;
  if (value.includes('\0')) return false;
  if (/\s/.test(value)) return false;
  const segments = value.split('/');
  for (const seg of segments) {
    if (seg === '' || seg === '..') return false;
    // `.` is permitted as the first segment for the canonical `.` /
    // `./api` forms; reject `.` as any other segment to keep paths normal.
    if (seg === '.' && segments.indexOf(seg) !== 0) return false;
    if (!/^[a-zA-Z0-9._-]+$/.test(seg)) return false;
  }
  return true;
}

function formatSourceShapeError(serviceName: string): string {
  return [
    `Service '${serviceName}' has an invalid 'source:' value.`,
    '',
    `  The 'source:' block must be an object. The only field accepted in`,
    `  this release is 'path' — a relative directory under the project root.`,
    '',
    `  Example:`,
    `    services:`,
    `      ${serviceName}:`,
    `        type: node`,
    `        source:`,
    `          path: ./api`,
  ].join('\n');
}

function formatSourcePathError(serviceName: string, value: unknown): string {
  return [
    `Service '${serviceName}' has an invalid 'source.path' value: ${JSON.stringify(value)}.`,
    '',
    `  The 'source.path' field is optional. When set, it must be a relative`,
    `  directory name — no leading '/', no '..' segments, no whitespace,`,
    `  no shell metacharacters.`,
    '',
    `  Example:`,
    `    services:`,
    `      ${serviceName}:`,
    `        type: node`,
    `        source:`,
    `          path: ./api`,
  ].join('\n');
}

function formatSourceUnsupportedKeyError(serviceName: string, key: string): string {
  return [
    `Service '${serviceName}' has an unsupported key 'source.${key}'.`,
    '',
    `  The only field accepted in this release is 'source.path'. Future`,
    `  fields (e.g. 'source.git') require a schema bump and are rejected`,
    `  here so a typo cannot silently change deploy behavior.`,
    '',
    `  Example:`,
    `    services:`,
    `      ${serviceName}:`,
    `        type: node`,
    `        source:`,
    `          path: ./api`,
  ].join('\n');
}

/**
 * Backlog 189 Level 1 — error text for a malformed `depends_on:` value. The
 * field is optional; when set it must be a list of strings. Wording follows
 * §23 voice — plain, specific, no exclamation marks.
 */
function formatDependsOnShapeError(serviceName: string): string {
  return [
    `Service '${serviceName}' has an invalid 'depends_on:' value.`,
    '',
    `  The 'depends_on:' field is optional. When set, it must be a list of`,
    `  service names — strings only.`,
    '',
    `  Example:`,
    `    services:`,
    `      redis:`,
    `        type: node`,
    `      ${serviceName}:`,
    `        type: node`,
    `        path: /`,
    `        depends_on: [redis]`,
  ].join('\n');
}

function formatDependsOnSelfReferenceError(serviceName: string): string {
  return [
    `Service '${serviceName}' lists itself in 'depends_on:'.`,
    '',
    `  A service cannot depend on itself. Remove '${serviceName}' from the`,
    `  list — or, if you meant to depend on a different service, fix the`,
    `  reference.`,
  ].join('\n');
}

function formatDependsOnDuplicateError(serviceName: string, dep: string): string {
  return [
    `Service '${serviceName}' lists '${dep}' more than once in 'depends_on:'.`,
    '',
    `  Each dependency should appear at most once. Remove the duplicate.`,
  ].join('\n');
}

function formatDependsOnUnknownTargetError(
  serviceName: string,
  dep: string,
  services: string[],
  privateRequires: string[],
): string {
  // Backlog 201 — `depends_on:` accepts both peer services AND private-
  // requires resources (e.g. `redis`, `mongodb` declared via
  // `requires: [redis: private]`). List both kinds so the user sees every
  // valid target, not just service names.
  const knownServices = services.filter((n) => n !== serviceName).sort();
  const knownPrivate = [...privateRequires].sort();
  const lines: string[] = [];
  if (knownServices.length > 0) {
    lines.push(`  Known services: ${knownServices.join(', ')}.`);
  }
  if (knownPrivate.length > 0) {
    lines.push(`  Known private requires: ${knownPrivate.join(', ')}.`);
  }
  if (lines.length === 0) {
    lines.push(`  No other services or private requires are declared in this yaml.`);
  }
  return [
    `Service '${serviceName}' has depends_on '${dep}', but no service or`,
    `  private-requires resource by that name is declared in this`,
    `  cloudgrid.yaml.`,
    '',
    ...lines,
  ].join('\n');
}

function formatDependsOnCronTargetError(serviceName: string, dep: string): string {
  return [
    `Service '${serviceName}' has depends_on '${dep}', but '${dep}' is a cron`,
    `  service. Cron services run on a schedule and have no Service to wait`,
    `  on, so the startup-ordering primitive does not apply here.`,
    '',
    `  Drop '${dep}' from depends_on. If you need '${dep}' to have run before`,
    `  '${serviceName}' starts, model it differently (manual sequencing, or`,
    `  a one-shot init job) — that pattern is out of scope for this release.`,
  ].join('\n');
}

/**
 * Decision 023 Update 2026-05-18 — error text for an invalid top-level
 * `vault:` block. Must be a map of `<ENV_VAR_NAME>: <vault_item_key>`.
 * Wording follows §23 voice — plain, specific, no exclamation marks.
 */
function formatVaultShapeError(): string {
  return [
    `Invalid 'vault:' value in cloudgrid.yaml.`,
    '',
    `  The 'vault:' field is optional. When set, it must be a map of`,
    `  ENV_VAR_NAME -> vault-item-key. The key on the left is the name the`,
    `  resolved value will be injected as in your pod env; the value on the`,
    `  right is the key of an existing item in your org's vault.`,
    '',
    `  Example:`,
    `    vault:`,
    `      STRIPE_SECRET_KEY: stripe_secret`,
    `      DATABASE_URL: pg_url`,
  ].join('\n');
}

function formatVaultEnvNameError(envName: string): string {
  return [
    `Invalid vault env-var name: ${JSON.stringify(envName)}.`,
    '',
    `  Vault env-var names must start with an uppercase letter and contain`,
    `  only uppercase letters, digits, and underscores (max 64 characters).`,
    '',
    `  Example:`,
    `    vault:`,
    `      STRIPE_SECRET_KEY: stripe_secret`,
  ].join('\n');
}

function formatVaultItemKeyError(envName: string, value: unknown): string {
  return [
    `Invalid vault item key for ${JSON.stringify(envName)}: ${JSON.stringify(value)}.`,
    '',
    `  Each value in 'vault:' must be the key of an item in your org's`,
    `  vault. Keys are 1..64 characters of uppercase letters, digits, and`,
    `  underscores.`,
    '',
    `  Example:`,
    `    vault:`,
    `      ${VAULT_ENV_NAME_PATTERN.test(envName) ? envName : 'STRIPE_SECRET_KEY'}: stripe_secret`,
  ].join('\n');
}

function isGcpRequires(entry: unknown): entry is GcpRequires {
  return (
    typeof entry === 'object' &&
    entry !== null &&
    'gcp' in entry &&
    typeof (entry as any).gcp === 'object' &&
    (entry as any).gcp !== null &&
    Array.isArray((entry as any).gcp.services)
  );
}

/**
 * Backlog 192 phase 7 — removal-notice error for the top-level
 * `internal_urls:` field. 0.8.17 shipped a one-cycle WARN; 0.8.18 flips
 * to a hard error so the auth-less stop-gap stops being usable. 0.8.19
 * removes the field from the schema entirely. Wording follows §23 voice —
 * plain, specific, confident, no exclamation marks.
 */
function formatInternalUrlsRemovedError(): string {
  return [
    `'internal_urls:' is no longer supported.`,
    '',
    `  Migrate to 'calls:' for auth-bearing inter-app calls. Each entry is`,
    `  a full caller address.`,
    '',
    `  cloudgrid.yaml:`,
    `    calls:`,
    `      - org:<org-slug>/<entity-slug>`,
    '',
    `  Contract: docs/contracts/caller-tokens.md.`,
    `  The field will be removed entirely in 0.8.19.`,
  ].join('\n');
}

// ── Backlog 192 phase 2 — callers: / calls: shape validation ────────────────

// Decision 027a §"yaml schema": same DNS-1123 slug shape used everywhere
// else in the platform. Kept in this module (rather than importing from
// caller-address.ts) so the validator has a single self-contained rejection
// path without pulling the runtime helper into the yaml-validation surface.
const ORG_ADDRESS_RE = /^org:[a-z0-9-]+\/[a-z0-9-]+$/;
const SPACE_ADDRESS_RE = /^space:[a-z0-9-]+\/([a-z0-9-]+|\*)$/;

function validateCallersBlock(raw: unknown): void {
  if (raw === undefined) return;
  if (!isPlainObject(raw)) {
    die(formatCallersShapeError());
  }
  const cfg = raw as Record<string, unknown>;
  const policy = cfg.policy;
  if (policy !== undefined) {
    if (typeof policy !== 'string' || !(CALLERS_POLICY_VALUES as readonly string[]).includes(policy)) {
      die(formatCallersEnumError('policy', policy, CALLERS_POLICY_VALUES));
    }
  }
  const scope = cfg.scope;
  if (scope !== undefined) {
    if (typeof scope !== 'string' || !(CALLERS_SCOPE_VALUES as readonly string[]).includes(scope)) {
      die(formatCallersEnumError('scope', scope, CALLERS_SCOPE_VALUES));
    }
  }
  const fallback = cfg.fallback;
  if (fallback !== undefined) {
    if (typeof fallback !== 'string' || !(CALLERS_FALLBACK_VALUES as readonly string[]).includes(fallback)) {
      die(formatCallersEnumError('fallback', fallback, CALLERS_FALLBACK_VALUES));
    }
  }

  // allowed_apps coupling:
  //   scope=explicit → REQUIRES non-empty allowed_apps
  //   scope=org|space (or absent → default 'org') → FORBIDS allowed_apps
  const effectiveScope = typeof scope === 'string' ? scope : 'org';
  const allowedApps = cfg.allowed_apps;
  if (effectiveScope === 'explicit') {
    if (!Array.isArray(allowedApps) || allowedApps.length === 0) {
      die(formatAllowedAppsRequiredError());
    }
    allowedApps.forEach((entry, idx) => {
      if (typeof entry !== 'string' || !isValidAllowedAppsAddress(entry)) {
        die(formatAllowedAppsEntryError(idx, entry));
      }
    });
  } else if (allowedApps !== undefined) {
    die(formatAllowedAppsForbiddenError(effectiveScope));
  }
}

function validateCallsBlock(raw: unknown): void {
  if (raw === undefined) return;
  if (!Array.isArray(raw)) {
    die(formatCallsShapeError());
  }
  const seen = new Set<string>();
  raw.forEach((entry, idx) => {
    if (typeof entry !== 'string' || !ORG_ADDRESS_RE.test(entry)) {
      die(formatCallsEntryError(idx, entry));
    }
    if (seen.has(entry)) {
      die(formatCallsDuplicateError(entry));
    }
    seen.add(entry);
  });
}

function isValidAllowedAppsAddress(addr: string): boolean {
  return ORG_ADDRESS_RE.test(addr) || SPACE_ADDRESS_RE.test(addr);
}

function formatCallersShapeError(): string {
  return [
    `Invalid 'callers:' value.`,
    '',
    `  The 'callers:' block declares which agents may call this entity. It`,
    `  must be a map with optional 'policy', 'scope', 'fallback', and`,
    `  (when scope=explicit) 'allowed_apps:'.`,
    '',
    `  Example:`,
    `    callers:`,
    `      policy: auto         # auto | manual (default: manual)`,
    `      scope: org           # org | space | explicit (default: org)`,
    `      fallback: deny       # deny | warn (default: deny)`,
  ].join('\n');
}

function formatCallersEnumError(
  field: string,
  value: unknown,
  allowed: readonly string[],
): string {
  const allowedList = allowed.map((v) => `'${v}'`).join(' | ');
  return [
    `Invalid 'callers.${field}' value: ${JSON.stringify(value)}.`,
    '',
    `  'callers.${field}' must be one of: ${allowedList}.`,
    '',
    `  Example:`,
    `    callers:`,
    `      ${field}: ${allowed[0]}`,
  ].join('\n');
}

function formatAllowedAppsRequiredError(): string {
  return [
    `'callers.scope: explicit' requires a non-empty 'allowed_apps' list.`,
    '',
    `  When scope is 'explicit' the allow-set is defined entry-by-entry — the`,
    `  platform doesn't infer it from org or space membership. Add at least`,
    `  one address, or change scope to 'org' or 'space' to inherit membership.`,
    '',
    `  Example:`,
    `    callers:`,
    `      scope: explicit`,
    `      allowed_apps:`,
    `        - org:atomic/billing-service`,
    `        - space:marketing/*`,
  ].join('\n');
}

function formatAllowedAppsForbiddenError(scope: string): string {
  return [
    `'allowed_apps' is only valid when 'callers.scope: explicit'.`,
    '',
    `  Current scope is '${scope}', which derives the allow-set from`,
    `  membership (any entity in the same ${scope === 'space' ? 'space' : 'org'} may`,
    `  request a grant). Listing 'allowed_apps:' alongside this scope would`,
    `  be ambiguous — drop one of the two.`,
    '',
    `  Pick one:`,
    `    1. Remove 'allowed_apps' to use the ${scope}-wide allow-set.`,
    `    2. Change scope to 'explicit' to make the allow-set entry-by-entry.`,
  ].join('\n');
}

function formatAllowedAppsEntryError(idx: number, value: unknown): string {
  return [
    `Invalid 'callers.allowed_apps[${idx}]' value: ${JSON.stringify(value)}.`,
    '',
    `  Each entry must be one of:`,
    `    org:<org-slug>/<entity-slug>     (e.g. org:atomic/billing-service)`,
    `    space:<space-slug>/<entity-slug> (e.g. space:marketing/dashboards)`,
    `    space:<space-slug>/*             (every entity in a space)`,
    '',
    `  Slugs are lowercase letters, digits, and hyphens — the same shape used`,
    `  for entity and org names elsewhere in cloudgrid.yaml.`,
  ].join('\n');
}

function formatCallsShapeError(): string {
  return [
    `'calls' must be a list of caller-model addresses.`,
    '',
    `  The 'calls:' block declares which other entities this one needs to`,
    `  call. The deployer requests a grant from each target at plug time and`,
    `  injects 'CLOUDGRID_CALLER_TOKEN_<ADDR>' into this entity's env on`,
    `  approval.`,
    '',
    `  Example:`,
    `    calls:`,
    `      - org:atomic/content-aggregator`,
    `      - org:atomic/billing-service`,
  ].join('\n');
}

function formatCallsEntryError(idx: number, value: unknown): string {
  return [
    `Invalid 'calls[${idx}]' value: ${JSON.stringify(value)}.`,
    '',
    `  Each 'calls' entry must be a fully-qualified entity address:`,
    `    org:<org-slug>/<entity-slug>`,
    '',
    `  Outbound calls must point at a concrete entity. 'space:' wildcards`,
    `  and bare entity names are not accepted on this side — only on the`,
    `  callee's 'allowed_apps:'.`,
  ].join('\n');
}

function formatCallsDuplicateError(addr: string): string {
  return [
    `'calls' lists '${addr}' more than once.`,
    '',
    `  Each address should appear at most once. Remove the duplicate.`,
  ].join('\n');
}

// Backlog 473 — compile-time proof that PERSIST_ENTRY_KEYS (the imperative
// strict-keys set) stays in lock-step with the PersistDeclaration interface.
// The validator is hand-rolled (no zod here), so the key set and the
// `{name, path, size, mode}` destructure in validatePersist are not
// type-coupled to the interface on their own. This guard makes them so: add
// a field to PersistDeclaration without adding it here (or vice versa) and
// tsc fails on the object literal. Mirrors the `_schemaMatchesInterface`
// proof in packages/api/src/schemas/platform-settings-schema.ts, adapted to
// a key-coverage shape because the validator is imperative, not zod-derived.
type _PersistEntryKeys = { [K in keyof Required<PersistDeclaration>]: true };
const _persistKeyCheck: _PersistEntryKeys = {
  name: true,
  path: true,
  size: true,
  mode: true,
};
void _persistKeyCheck;
